/**
 * @file Core constants — zero dependencies.
 * @module tree/constants
 * @license AGPL-3.0-only
 */

// Coordinate spaces
const WORLD  = 'WORLD';
const EYE    = 'EYE';
const NDC    = 'NDC';
const SCREEN = 'SCREEN';
const MODEL  = 'MODEL';
const MATRIX = 'MATRIX';

// NDC Z convention (only difference between backends)
const WEBGL  = -1;   // z ∈ [−1, 1]
const WEBGPU =  0;   // z ∈ [0, 1]

// Visibility results
const INVISIBLE   = 0;
const VISIBLE     = 1;
const SEMIVISIBLE = 2;

// Basis vectors (frozen plain arrays — duck-typed Vec3)
const ORIGIN = Object.freeze([0, 0, 0]);
const i  = Object.freeze([1, 0, 0]);
const j  = Object.freeze([0, 1, 0]);
const k  = Object.freeze([0, 0, 1]);
const _i = Object.freeze([-1, 0, 0]);
const _j = Object.freeze([0, -1, 0]);
const _k = Object.freeze([0, 0, -1]);

/**
 * @file Quaternion algebra and mat4/mat3 conversions.
 * @module tree/quat
 * @license AGPL-3.0-only
 *
 * Quaternions are stored as flat [x, y, z, w] arrays (w-last, glTF layout).
 *
 * All functions follow the out-first, zero-allocation contract.
 * Conversion functions bridge between quaternion and matrix representations
 * but do not perform any higher-level graphics operations — those belong
 * in form.js (matrix construction from specs) or track.js (animation).
 */


// =========================================================================
// Basic ops
// =========================================================================

/** Set all four components. @returns {number[]} out */
const qSet = (out, x, y, z, w) => {
  out[0] = x; out[1] = y; out[2] = z; out[3] = w; return out;
};

/** Copy quaternion a into out. @returns {number[]} out */
const qCopy = (out, a) => {
  out[0] = a[0]; out[1] = a[1]; out[2] = a[2]; out[3] = a[3]; return out;
};

/** Dot product of two quaternions. */
const qDot = (a, b) => a[0]*b[0] + a[1]*b[1] + a[2]*b[2] + a[3]*b[3];

/** Normalise quaternion in-place. @returns {number[]} out */
const qNormalize = (out) => {
  const l = Math.sqrt(out[0]*out[0]+out[1]*out[1]+out[2]*out[2]+out[3]*out[3]) || 1;
  out[0]/=l; out[1]/=l; out[2]/=l; out[3]/=l; return out;
};

/** Negate quaternion (same rotation, different hemisphere). @returns {number[]} out */
const qNegate = (out, a) => {
  out[0]=-a[0]; out[1]=-a[1]; out[2]=-a[2]; out[3]=-a[3]; return out;
};

/** Hamilton product out = a * b. @returns {number[]} out */
const qMul = (out, a, b) => {
  const ax=a[0],ay=a[1],az=a[2],aw=a[3], bx=b[0],by=b[1],bz=b[2],bw=b[3];
  out[0]=aw*bx+ax*bw+ay*bz-az*by;
  out[1]=aw*by-ax*bz+ay*bw+az*bx;
  out[2]=aw*bz+ax*by-ay*bx+az*bw;
  out[3]=aw*bw-ax*bx-ay*by-az*bz;
  return out;
};

// =========================================================================
// Interpolation
// =========================================================================

/** Spherical linear interpolation. @returns {number[]} out */
const qSlerp = (out, a, b, t) => {
  let bx=b[0],by=b[1],bz=b[2],bw=b[3];
  let d = a[0]*bx+a[1]*by+a[2]*bz+a[3]*bw;
  if (d < 0) { bx=-bx; by=-by; bz=-bz; bw=-bw; d=-d; }
  let f0, f1;
  if (1-d > 1e-10) {
    const th=Math.acos(d), st=Math.sin(th);
    f0=Math.sin((1-t)*th)/st; f1=Math.sin(t*th)/st;
  } else {
    f0=1-t; f1=t;
  }
  out[0]=a[0]*f0+bx*f1; out[1]=a[1]*f0+by*f1;
  out[2]=a[2]*f0+bz*f1; out[3]=a[3]*f0+bw*f1;
  return qNormalize(out);
};

/**
 * Normalised linear interpolation (nlerp).
 * Cheaper than slerp; slightly non-constant angular velocity.
 * Handles antipodal quats by flipping b when dot < 0.
 * @returns {number[]} out
 */
const qNlerp = (out, a, b, t) => {
  let bx=b[0],by=b[1],bz=b[2],bw=b[3];
  if (a[0]*bx+a[1]*by+a[2]*bz+a[3]*bw < 0) { bx=-bx; by=-by; bz=-bz; bw=-bw; }
  out[0]=a[0]+t*(bx-a[0]); out[1]=a[1]+t*(by-a[1]);
  out[2]=a[2]+t*(bz-a[2]); out[3]=a[3]+t*(bw-a[3]);
  return qNormalize(out);
};

// =========================================================================
// Construction
// =========================================================================

/**
 * Build a quaternion from axis-angle.
 * @param {number[]} out
 * @param {number} ax @param {number} ay @param {number} az  Axis (need not be unit).
 * @param {number} angle  Radians.
 * @returns {number[]} out
 */
const qFromAxisAngle = (out, ax, ay, az, angle) => {
  const half = angle * 0.5;
  const s    = Math.sin(half);
  const len  = Math.sqrt(ax*ax + ay*ay + az*az) || 1;
  out[0] = s * ax / len; out[1] = s * ay / len; out[2] = s * az / len;
  out[3] = Math.cos(half);
  return out;
};

/**
 * Build a quaternion from a look direction (−Z forward) and optional up (default +Y).
 * @param {number[]} out
 * @param {number[]} dir  Forward direction [x,y,z].
 * @param {number[]} [up] Up vector [x,y,z].
 * @returns {number[]} out
 */
const qFromLookDir = (out, dir, up) => {
  let fx=dir[0],fy=dir[1],fz=dir[2];
  const fl=Math.sqrt(fx*fx+fy*fy+fz*fz)||1;
  fx/=fl; fy/=fl; fz/=fl;
  let ux=up?up[0]:0, uy=up?up[1]:1, uz=up?up[2]:0;
  let rx=uy*fz-uz*fy, ry=uz*fx-ux*fz, rz=ux*fy-uy*fx;
  const rl=Math.sqrt(rx*rx+ry*ry+rz*rz)||1;
  rx/=rl; ry/=rl; rz/=rl;
  ux=fy*rz-fz*ry; uy=fz*rx-fx*rz; uz=fx*ry-fy*rx;
  return qFromRotMat3x3(out, rx,ry,rz, ux,uy,uz, -fx,-fy,-fz);
};

/**
 * Build a quaternion from a 3×3 rotation matrix (9 row-major scalars).
 * @returns {number[]} out (normalised)
 */
const qFromRotMat3x3 = (out, m00,m01,m02, m10,m11,m12, m20,m21,m22) => {
  const tr = m00+m11+m22;
  if (tr > 0) {
    const s=0.5/Math.sqrt(tr+1);
    out[3]=0.25/s; out[0]=(m21-m12)*s; out[1]=(m02-m20)*s; out[2]=(m10-m01)*s;
  } else if (m00>m11 && m00>m22) {
    const s=2*Math.sqrt(1+m00-m11-m22);
    out[3]=(m21-m12)/s; out[0]=0.25*s; out[1]=(m01+m10)/s; out[2]=(m02+m20)/s;
  } else if (m11>m22) {
    const s=2*Math.sqrt(1+m11-m00-m22);
    out[3]=(m02-m20)/s; out[0]=(m01+m10)/s; out[1]=0.25*s; out[2]=(m12+m21)/s;
  } else {
    const s=2*Math.sqrt(1+m22-m00-m11);
    out[3]=(m10-m01)/s; out[0]=(m02+m20)/s; out[1]=(m12+m21)/s; out[2]=0.25*s;
  }
  return qNormalize(out);
};

/**
 * Extract a unit quaternion from the upper-left 3×3 of a column-major mat4.
 * @param {number[]} out
 * @param {Float32Array|number[]} m  Column-major mat4.
 * @returns {number[]} out
 */
const qFromMat4 = (out, m) =>
  qFromRotMat3x3(out, m[0],m[4],m[8], m[1],m[5],m[9], m[2],m[6],m[10]);

/**
 * Write a quaternion into the rotation block of a column-major mat4.
 * Translation and perspective rows/cols are set to identity values.
 * @param {Float32Array|number[]} out  16-element array.
 * @param {number[]} q  [x,y,z,w].
 * @returns {Float32Array|number[]} out
 */
const qToMat4 = (out, q) => {
  const x=q[0],y=q[1],z=q[2],w=q[3];
  const x2=x+x,y2=y+y,z2=z+z;
  const xx=x*x2,xy=x*y2,xz=x*z2,yy=y*y2,yz=y*z2,zz=z*z2,wx=w*x2,wy=w*y2,wz=w*z2;
  out[0]=1-(yy+zz); out[1]=xy+wz;     out[2]=xz-wy;     out[3]=0;
  out[4]=xy-wz;     out[5]=1-(xx+zz); out[6]=yz+wx;     out[7]=0;
  out[8]=xz+wy;     out[9]=yz-wx;     out[10]=1-(xx+yy); out[11]=0;
  out[12]=0;        out[13]=0;        out[14]=0;          out[15]=1;
  return out;
};

// =========================================================================
// Decomposition
// =========================================================================

/**
 * Decompose a unit quaternion into { axis:[x,y,z], angle } (radians).
 * @param {number[]} q  [x,y,z,w].
 * @param {Object}  [out]
 * @returns {{ axis: number[], angle: number }}
 */
const quatToAxisAngle = (q, out) => {
  out = out || {};
  const x=q[0],y=q[1],z=q[2],w=q[3];
  const sinHalf = Math.sqrt(x*x+y*y+z*z);
  if (sinHalf < 1e-8) { out.axis=[0,1,0]; out.angle=0; return out; }
  out.angle = 2*Math.atan2(sinHalf, w);
  out.axis  = [x/sinHalf, y/sinHalf, z/sinHalf];
  return out;
};

/**
 * @file Matrix construction from geometric specs and partial decomposition.
 * @module tree/form
 * @license AGPL-3.0-only
 *
 * Constructs mat4s from higher-level specs: TRS transforms, orthonormal
 * bases, lookat parameters, projection parameters, and special-purpose
 * matrices (bias, reflection).
 *
 * Design invariant: form.js has no dependency on query.js. Construction
 * from specs requires only scalar arithmetic and quaternion conversions.
 * Callers compose the resulting matrices using query.js (mat4Mul etc.).
 *
 * ── NDC Z convention ──────────────────────────────────────────────────────
 * Controlled by `ndcZMin` in every projection constructor:
 *   WEBGL  = −1   near → NDC z = −1,  far → NDC z = +1
 *   WEBGPU =  0   near → NDC z =  0,  far → NDC z = +1
 *
 * ── NDC Y convention ──────────────────────────────────────────────────────
 * Controlled by `ndcYSign` in every projection constructor (default +1):
 *   +1  NDC y-up   — standard: OpenGL / WebGL / WebGPU browser / Three.js / p5v2
 *   −1  NDC y-down — native Vulkan clip space
 *
 * Negating ndcYSign flips row 1 of the projection matrix (elements
 * out[1], out[5], out[9], out[13]), reversing the y-axis in clip space.
 * mat4View, mat4Eye, and all non-projection constructors are convention-
 * agnostic — they produce the same matrix regardless of the NDC y direction.
 *
 * ── Screen Y convention ───────────────────────────────────────────────────
 * Screen-y direction (DOM y-down vs OpenGL y-up) is a separate concern from
 * NDC-y direction and is handled in query.js via the signed viewport height.
 * See the query.js module header for details.
 *
 * All functions follow the out-first, zero-allocation contract.
 */


// =========================================================================
// Frame construction
// =========================================================================

/**
 * Rigid frame from orthonormal basis + translation.
 * Column-major layout: col0=right, col1=up, col2=forward, col3=translation.
 *
 * @param {Float32Array|number[]} out  16-element destination.
 * @param {number} rx,ry,rz  Right vector (col 0).
 * @param {number} ux,uy,uz  Up vector    (col 1).
 * @param {number} fx,fy,fz  Forward vec  (col 2).
 * @param {number} tx,ty,tz  Translation  (col 3).
 */
function mat4FromBasis(out, rx,ry,rz, ux,uy,uz, fx,fy,fz, tx,ty,tz) {
  out[0]=rx;  out[1]=ry;  out[2]=rz;  out[3]=0;
  out[4]=ux;  out[5]=uy;  out[6]=uz;  out[7]=0;
  out[8]=fx;  out[9]=fy;  out[10]=fz; out[11]=0;
  out[12]=tx; out[13]=ty; out[14]=tz; out[15]=1;
  return out;
}

/**
 * View matrix (world→eye) from lookat parameters.
 * Camera looks along −Z in eye space; right = normalize(up × (−Z)).
 * Cheaper than building the eye matrix and inverting.
 *
 * @param {Float32Array|number[]} out  16-element destination.
 * @param {number} ex,ey,ez   Eye (camera) position.
 * @param {number} cx,cy,cz   Look-at target.
 * @param {number} ux,uy,uz   World up hint (need not be unit).
 */
function mat4View(out, ex,ey,ez, cx,cy,cz, ux,uy,uz) {
  let zx=ex-cx, zy=ey-cy, zz=ez-cz;
  const zl=Math.sqrt(zx*zx+zy*zy+zz*zz)||1;
  zx/=zl; zy/=zl; zz/=zl;
  let xx=uy*zz-uz*zy, xy=uz*zx-ux*zz, xz=ux*zy-uy*zx;
  const xl=Math.sqrt(xx*xx+xy*xy+xz*xz)||1;
  xx/=xl; xy/=xl; xz/=xl;
  const yx=zy*xz-zz*xy, yy=zz*xx-zx*xz, yz=zx*xy-zy*xx;
  out[0]=xx;              out[1]=yx;              out[2]=zx;              out[3]=0;
  out[4]=xy;              out[5]=yy;              out[6]=zy;              out[7]=0;
  out[8]=xz;              out[9]=yz;              out[10]=zz;             out[11]=0;
  out[12]=-(xx*ex+xy*ey+xz*ez);
  out[13]=-(yx*ex+yy*ey+yz*ez);
  out[14]=-(zx*ex+zy*ey+zz*ez);
  out[15]=1;
  return out;
}

/**
 * Eye matrix (eye→world) from lookat parameters.
 * Transpose of the rotation block + direct translation column.
 * Same parameters as mat4View.
 *
 * @param {Float32Array|number[]} out  16-element destination.
 * @param {number} ex,ey,ez   Eye position.
 * @param {number} cx,cy,cz   Look-at target.
 * @param {number} ux,uy,uz   World up hint.
 */
function mat4Eye(out, ex,ey,ez, cx,cy,cz, ux,uy,uz) {
  let zx=ex-cx, zy=ey-cy, zz=ez-cz;
  const zl=Math.sqrt(zx*zx+zy*zy+zz*zz)||1;
  zx/=zl; zy/=zl; zz/=zl;
  let xx=uy*zz-uz*zy, xy=uz*zx-ux*zz, xz=ux*zy-uy*zx;
  const xl=Math.sqrt(xx*xx+xy*xy+xz*xz)||1;
  xx/=xl; xy/=xl; xz/=xl;
  const yx=zy*xz-zz*xy, yy=zz*xx-zx*xz, yz=zx*xy-zy*xx;
  out[0]=xx;  out[1]=xy;  out[2]=xz;  out[3]=0;
  out[4]=yx;  out[5]=yy;  out[6]=yz;  out[7]=0;
  out[8]=zx;  out[9]=zy;  out[10]=zz; out[11]=0;
  out[12]=ex; out[13]=ey; out[14]=ez; out[15]=1;
  return out;
}

// =========================================================================
// TRS construction
// =========================================================================

/**
 * Column-major mat4 from flat TRS scalars. No struct allocation.
 *
 * @param {Float32Array|number[]} out  16-element destination.
 * @param {number} tx,ty,tz      Translation.
 * @param {number} qx,qy,qz,qw   Rotation quaternion [x,y,z,w].
 * @param {number} sx,sy,sz      Scale.
 */
function mat4FromTRS(out, tx,ty,tz, qx,qy,qz,qw, sx,sy,sz) {
  const x2=qx+qx,y2=qy+qy,z2=qz+qz;
  const xx=qx*x2,xy=qx*y2,xz=qx*z2,yy=qy*y2,yz=qy*z2,zz=qz*z2;
  const wx=qw*x2,wy=qw*y2,wz=qw*z2;
  out[0]=(1-(yy+zz))*sx; out[1]=(xy+wz)*sx;      out[2]=(xz-wy)*sx;      out[3]=0;
  out[4]=(xy-wz)*sy;     out[5]=(1-(xx+zz))*sy;  out[6]=(yz+wx)*sy;      out[7]=0;
  out[8]=(xz+wy)*sz;     out[9]=(yz-wx)*sz;      out[10]=(1-(xx+yy))*sz; out[11]=0;
  out[12]=tx; out[13]=ty; out[14]=tz; out[15]=1;
  return out;
}

/**
 * Translation-only mat4.
 * @param {Float32Array|number[]} out  16-element destination.
 * @param {number} tx,ty,tz
 */
function mat4FromTranslation(out, tx,ty,tz) {
  out[0]=1; out[1]=0; out[2]=0;  out[3]=0;
  out[4]=0; out[5]=1; out[6]=0;  out[7]=0;
  out[8]=0; out[9]=0; out[10]=1; out[11]=0;
  out[12]=tx; out[13]=ty; out[14]=tz; out[15]=1;
  return out;
}

/**
 * Scale-only mat4.
 * @param {Float32Array|number[]} out  16-element destination.
 * @param {number} sx,sy,sz
 */
function mat4FromScale(out, sx,sy,sz) {
  out[0]=sx; out[1]=0;  out[2]=0;   out[3]=0;
  out[4]=0;  out[5]=sy; out[6]=0;   out[7]=0;
  out[8]=0;  out[9]=0;  out[10]=sz; out[11]=0;
  out[12]=0; out[13]=0; out[14]=0;  out[15]=1;
  return out;
}

// =========================================================================
// Projection construction
// =========================================================================

/**
 * Orthographic projection matrix.
 *
 * @param {Float32Array|number[]} out  16-element destination.
 * @param {number} left,right,bottom,top  Frustum extents.
 * @param {number} near,far              Clip plane distances (positive).
 * @param {number} ndcZMin               −1 (WEBGL) or 0 (WEBGPU).
 * @param {number} [ndcYSign=1]          +1 = NDC y-up (default); −1 = NDC y-down (native Vulkan).
 */
function mat4Ortho(out, left, right, bottom, top, near, far, ndcZMin, ndcYSign=1) {
  const rl=1/(right-left), tb=1/(top-bottom), fn=1/(far-near);
  out[0]=2*rl;              out[1]=0;                       out[2]=0;               out[3]=0;
  out[4]=0;                 out[5]=ndcYSign*2*tb;           out[6]=0;               out[7]=0;
  out[8]=0;                 out[9]=0;                       out[10]=(ndcZMin-1)*fn; out[11]=0;
  out[12]=-(right+left)*rl; out[13]=ndcYSign*(-(top+bottom)*tb);
  out[14]=(ndcZMin*far-near)*fn;
  out[15]=1;
  return out;
}

/**
 * Perspective projection matrix (general / off-centre frustum).
 * Symmetric case: left=-right, bottom=-top — derive from fov+aspect in user space:
 *   top = near * Math.tan(fov / 2);  right = top * aspect
 *   mat4Persp(out, -right, right, -top, top, near, far, ndcZMin)
 *
 * @param {Float32Array|number[]} out  16-element destination.
 * @param {number} left,right,bottom,top  Near-plane extents (signed, y-up: top>0, bottom<0).
 * @param {number} near,far              Clip plane distances (positive).
 * @param {number} ndcZMin               −1 (WEBGL) or 0 (WEBGPU).
 * @param {number} [ndcYSign=1]          +1 = NDC y-up (default); −1 = NDC y-down (native Vulkan).
 */
function mat4Persp(out, left, right, bottom, top, near, far, ndcZMin, ndcYSign=1) {
  const rl=1/(right-left), tb=1/(top-bottom);
  out[0]=2*near*rl;        out[1]=0;                          out[2]=0;  out[3]=0;
  out[4]=0;                out[5]=ndcYSign*2*near*tb;         out[6]=0;  out[7]=0;
  out[8]=(right+left)*rl;  out[9]=ndcYSign*(top+bottom)*tb;
  out[10]=(ndcZMin*near-far)/(far-near);
  out[11]=-1;
  out[12]=0; out[13]=0;
  out[14]=(ndcZMin-1)*far*near/(far-near);
  out[15]=0;
  return out;
}

// =========================================================================
// Special-purpose construction
// =========================================================================

/**
 * Bias matrix: remaps xyz from NDC to texture/UV space [0, 1].
 * xy remap from [−1, 1]; z remaps from [ndcZMin, 1].
 * Used to convert light-space NDC coordinates to shadow map UV.
 *
 * Convention note: the standard bias maps NDC y = −1 → texture v = 0 and
 * NDC y = +1 → texture v = 1. This is correct for both NDC y-up and y-down
 * conventions because the shadow map was rendered with the same projection.
 *
 * Column-major (WEBGL, ndcZMin=−1):   Column-major (WEBGPU, ndcZMin=0):
 *   [ 0.5  0    0    0.5 ]              [ 0.5  0    0    0.5 ]
 *   [ 0    0.5  0    0.5 ]              [ 0    0.5  0    0.5 ]
 *   [ 0    0    0.5  0.5 ]              [ 0    0    1    0   ]
 *   [ 0    0    0    1   ]              [ 0    0    0    1   ]
 *
 * @param {Float32Array|number[]} out  16-element destination.
 * @param {number} ndcZMin  WEBGL (−1) or WEBGPU (0).
 */
function mat4Bias(out, ndcZMin) {
  const sz=1/(1-ndcZMin), tz=-ndcZMin/(1-ndcZMin);
  out[0]=0.5; out[1]=0;   out[2]=0;   out[3]=0;
  out[4]=0;   out[5]=0.5; out[6]=0;   out[7]=0;
  out[8]=0;   out[9]=0;   out[10]=sz; out[11]=0;
  out[12]=0.5; out[13]=0.5; out[14]=tz; out[15]=1;
  return out;
}

/**
 * Reflection matrix across a plane ax + by + cz = d.
 * [nx, ny, nz] must be a unit normal.
 *
 * @param {Float32Array|number[]} out  16-element destination.
 * @param {number} nx,ny,nz  Unit plane normal.
 * @param {number} d         Plane offset (dot(point_on_plane, normal)).
 */
function mat4Reflect(out, nx,ny,nz,d) {
  out[0]=1-2*nx*nx;  out[1]=-2*ny*nx;  out[2]=-2*nz*nx;  out[3]=0;
  out[4]=-2*nx*ny;   out[5]=1-2*ny*ny; out[6]=-2*nz*ny;  out[7]=0;
  out[8]=-2*nx*nz;   out[9]=-2*ny*nz;  out[10]=1-2*nz*nz; out[11]=0;
  out[12]=2*d*nx;    out[13]=2*d*ny;   out[14]=2*d*nz;   out[15]=1;
  return out;
}

/**
 * @file Matrix arithmetic, space-transform dispatch, and projection queries.
 * @module tree/query
 * @license AGPL-3.0-only
 *
 * The operative layer — receives matrices and extracts information.
 * Contrast with form.js which constructs matrices from specs.
 *
 *   form.js  — specs  → matrix
 *   query.js — matrix → information
 *
 * Storage: column-major Float32Array / ArrayLike<number>.
 * Multiply: mat4Mul(out, A, B) = A · B  (standard math order).
 * Pipeline: clip = P · V · M · v
 *
 * ── NDC Z convention ──────────────────────────────────────────────────────
 * Passed as `ndcZMin` to every space-transform function:
 *   WEBGL  = −1   z ∈ [−1,  1]
 *   WEBGPU =  0   z ∈ [ 0,  1]
 *
 * ── NDC Y convention ──────────────────────────────────────────────────────
 * Standard (OpenGL / WebGL / WebGPU browser / Three.js / p5v2):
 *   NDC y-up  — y = +1 at top, y = −1 at bottom.
 * Native Vulkan: NDC y-down — projections constructed with ndcYSign = −1
 * (see form.js).  Query functions are convention-agnostic: they work on
 * whatever matrices are passed in.
 *
 * ── Viewport convention ───────────────────────────────────────────────────
 * vp = [x, y, w, h] — w and h are SIGNED.
 *
 * The sign of h encodes the relationship between NDC y and screen y:
 *   h < 0 (e.g. −canvasH): screen y-DOWN  (DOM / p5 mouseX·mouseY / Vulkan surface)
 *                            NDC y=+1 → screen y=0  (top)
 *                            NDC y=−1 → screen y=H  (bottom)
 *   h > 0 (e.g. +canvasH): screen y-UP   (OpenGL desktop / WebGL gl_FragCoord)
 *                            NDC y=−1 → screen y=0  (bottom)
 *                            NDC y=+1 → screen y=H  (top)
 *
 * Pass [0, canvasH, canvasW, −canvasH] for p5/DOM coordinates.
 * Pass [0, 0, canvasW, canvasH] for WebGL gl_FragCoord / OpenGL bottom-left.
 * All helpers use vp[2]/vp[3] signed — no Math.abs — so both conventions
 * work automatically without any branching.
 *
 * All functions follow the out-first, zero-allocation contract.
 * Returns null on degeneracy (singular matrix, etc.).
 */


// ═══════════════════════════════════════════════════════════════════════════
// Mat4 arithmetic
// ═══════════════════════════════════════════════════════════════════════════

/** out = A · B  (column-major) */
function mat4Mul(out, A, B) {
  const a0=A[0],a1=A[1],a2=A[2],a3=A[3],
        a4=A[4],a5=A[5],a6=A[6],a7=A[7],
        a8=A[8],a9=A[9],a10=A[10],a11=A[11],
        a12=A[12],a13=A[13],a14=A[14],a15=A[15];
  let b0=B[0],b1=B[1],b2=B[2],b3=B[3];
  out[0]=a0*b0+a4*b1+a8*b2+a12*b3;
  out[1]=a1*b0+a5*b1+a9*b2+a13*b3;
  out[2]=a2*b0+a6*b1+a10*b2+a14*b3;
  out[3]=a3*b0+a7*b1+a11*b2+a15*b3;
  b0=B[4];b1=B[5];b2=B[6];b3=B[7];
  out[4]=a0*b0+a4*b1+a8*b2+a12*b3;
  out[5]=a1*b0+a5*b1+a9*b2+a13*b3;
  out[6]=a2*b0+a6*b1+a10*b2+a14*b3;
  out[7]=a3*b0+a7*b1+a11*b2+a15*b3;
  b0=B[8];b1=B[9];b2=B[10];b3=B[11];
  out[8]=a0*b0+a4*b1+a8*b2+a12*b3;
  out[9]=a1*b0+a5*b1+a9*b2+a13*b3;
  out[10]=a2*b0+a6*b1+a10*b2+a14*b3;
  out[11]=a3*b0+a7*b1+a11*b2+a15*b3;
  b0=B[12];b1=B[13];b2=B[14];b3=B[15];
  out[12]=a0*b0+a4*b1+a8*b2+a12*b3;
  out[13]=a1*b0+a5*b1+a9*b2+a13*b3;
  out[14]=a2*b0+a6*b1+a10*b2+a14*b3;
  out[15]=a3*b0+a7*b1+a11*b2+a15*b3;
  return out;
}

/** out = inverse(src). Returns null if singular (|det| < 1e-12). */
function mat4Invert(out, src) {
  const s0=src[0],s1=src[1],s2=src[2],s3=src[3],
        s4=src[4],s5=src[5],s6=src[6],s7=src[7],
        s8=src[8],s9=src[9],s10=src[10],s11=src[11],
        s12=src[12],s13=src[13],s14=src[14],s15=src[15];
  const b0=s0*s5-s1*s4, b1=s0*s6-s2*s4, b2=s0*s7-s3*s4,
        b3=s1*s6-s2*s5, b4=s1*s7-s3*s5, b5=s2*s7-s3*s6,
        b6=s8*s13-s9*s12, b7=s8*s14-s10*s12, b8=s8*s15-s11*s12,
        b9=s9*s14-s10*s13, b10=s9*s15-s11*s13, b11=s10*s15-s11*s14;
  let det=b0*b11-b1*b10+b2*b9+b3*b8-b4*b7+b5*b6;
  if (Math.abs(det) < 1e-12) return null;
  det = 1/det;
  out[0]=(s5*b11-s6*b10+s7*b9)*det;
  out[1]=(s2*b10-s1*b11-s3*b9)*det;
  out[2]=(s13*b5-s14*b4+s15*b3)*det;
  out[3]=(s10*b4-s9*b5-s11*b3)*det;
  out[4]=(s6*b8-s4*b11-s7*b7)*det;
  out[5]=(s0*b11-s2*b8+s3*b7)*det;
  out[6]=(s14*b2-s12*b5-s15*b1)*det;
  out[7]=(s8*b5-s10*b2+s11*b1)*det;
  out[8]=(s4*b10-s5*b8+s7*b6)*det;
  out[9]=(s1*b8-s0*b10-s3*b6)*det;
  out[10]=(s12*b4-s13*b2+s15*b0)*det;
  out[11]=(s9*b2-s8*b4-s11*b0)*det;
  out[12]=(s5*b7-s4*b9-s6*b6)*det;
  out[13]=(s0*b9-s1*b7+s2*b6)*det;
  out[14]=(s13*b1-s12*b3-s14*b0)*det;
  out[15]=(s8*b3-s9*b1+s10*b0)*det;
  return out;
}

/**
 * Normal matrix: inverseTranspose(upper-left 3×3 of src).
 * On degeneracy writes zeros and returns out.
 * @param {Float32Array|number[]} out  9-element destination.
 * @param {Float32Array|number[]} src  16-element mat4.
 */
function mat3NormalFromMat4(out, src) {
  const a00=src[0],a01=src[1],a02=src[2],
        a10=src[4],a11=src[5],a12=src[6],
        a20=src[8],a21=src[9],a22=src[10];
  const b01=a22*a11-a12*a21, b11=-a22*a01+a02*a21, b21=a12*a01-a02*a11;
  let det=a00*b01+a10*b11+a20*b21;
  if (Math.abs(det) < 1e-12) { for(let i=0;i<9;i++)out[i]=0; return out; }
  det=1/det;
  out[0]=b01*det;           out[1]=(-a22*a10+a12*a20)*det; out[2]=(a21*a10-a11*a20)*det;
  out[3]=b11*det;           out[4]=(a22*a00-a02*a20)*det;  out[5]=(-a21*a00+a01*a20)*det;
  out[6]=b21*det;           out[7]=(-a12*a00+a02*a10)*det; out[8]=(a11*a00-a01*a10)*det;
  return out;
}

/** out = mat4 * [x,y,z,1], perspective-divides, writes xyz. */
function mat4MulPoint(out, m, x, y, z) {
  const rx=m[0]*x+m[4]*y+m[8]*z+m[12], ry=m[1]*x+m[5]*y+m[9]*z+m[13],
        rz=m[2]*x+m[6]*y+m[10]*z+m[14], rw=m[3]*x+m[7]*y+m[11]*z+m[15];
  if (rw!==0&&rw!==1) { out[0]=rx/rw; out[1]=ry/rw; out[2]=rz/rw; }
  else                 { out[0]=rx;    out[1]=ry;    out[2]=rz;    }
  return out;
}

/**
 * Apply only the 3×3 linear block of a mat4 to a direction (no translation,
 * no perspective divide). Use mat3NormalFromMat4 for normals under non-uniform scale.
 */
function mat4MulDir(out, m, dx, dy, dz) {
  out[0]=m[0]*dx+m[4]*dy+m[8]*dz;
  out[1]=m[1]*dx+m[5]*dy+m[9]*dz;
  out[2]=m[2]*dx+m[6]*dy+m[10]*dz;
  return out;
}

// ═══════════════════════════════════════════════════════════════════════════
// Projection queries
// ═══════════════════════════════════════════════════════════════════════════

/** @returns {boolean} true if orthographic. */
function projIsOrtho(p) { return p[15] !== 0; }

/**
 * Near plane distance.
 * @param {ArrayLike<number>} p       Projection mat4.
 * @param {number}            ndcZMin WEBGL (−1) or WEBGPU (0).
 */
function projNear(p, ndcZMin) {
  return p[15]===0 ? p[14]/(p[10]+ndcZMin) : (p[14]-ndcZMin)/p[10];
}

/** Far plane distance (far always maps to NDC z = 1, convention-independent). */
function projFar(p) {
  return p[15]===0 ? p[14]/(1+p[10]) : (p[14]-1)/p[10];
}

function projLeft  (p, ndcZMin) { return p[15]===1 ? -(1+p[12])/p[0]  : projNear(p,ndcZMin)*(p[8]-1)/p[0];  }
function projRight (p, ndcZMin) { return p[15]===1 ?  (1-p[12])/p[0]  : projNear(p,ndcZMin)*(1+p[8])/p[0];  }

/**
 * Top extent of the near plane in camera space (y_max, positive for standard y-up camera).
 * Sign-normalized: returns the larger of the two y boundaries regardless of whether
 * the projection was built with ndcYSign = +1 or −1.
 */
function projTop(p, ndcZMin) {
  return p[15]===1
    ? ( Math.sign(p[5]) - p[13]) / p[5]   // ortho
    : projNear(p,ndcZMin)*(1+p[9])/p[5];  // perspective (p[5]>0 in practice)
}

/**
 * Bottom extent of the near plane in camera space (y_min, negative for standard y-up camera).
 */
function projBottom(p, ndcZMin) {
  return p[15]===1
    ? (-Math.sign(p[5]) - p[13]) / p[5]   // ortho
    : projNear(p,ndcZMin)*(p[9]-1)/p[5];  // perspective
}

/** Vertical field of view in radians (perspective only). */
function projFov (p) { return Math.abs(2*Math.atan(1/p[5])); }
/** Horizontal field of view in radians (perspective only). */
function projHfov(p) { return Math.abs(2*Math.atan(1/p[0])); }

// ═══════════════════════════════════════════════════════════════════════════
// Derived matrices
// ═══════════════════════════════════════════════════════════════════════════

/** out = P · V */
function mat4PV(out, proj, view)  { return mat4Mul(out, proj, view); }
/** out = V · M */
function mat4MV(out, model, view) { return mat4Mul(out, view, model); }

// ═══════════════════════════════════════════════════════════════════════════
// Frame-relative transforms
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Location transform between frames: out = inv(to) · from.
 * Assumes both matrices are affine (bottom row [0,0,0,1]).
 * @returns {ArrayLike<number>|null} out, or null if `to` is singular.
 */
function mat4Location(out, from, to) {
  // Same as: return mat4Invert(out, to) && mat4Mul(out, out, from);
  const a00=to[0],a01=to[1],a02=to[2],
        a10=to[4],a11=to[5],a12=to[6],
        a20=to[8],a21=to[9],a22=to[10];
  const b01=a22*a11-a12*a21, b11=a12*a20-a22*a10, b21=a21*a10-a11*a20;
  let det=a00*b01+a01*b11+a02*b21;
  if (Math.abs(det) < 1e-12) return null;
  det=1/det;
  const i00=b01*det,             i01=(a02*a21-a22*a01)*det, i02=(a12*a01-a02*a11)*det;
  const i10=b11*det,             i11=(a22*a00-a02*a20)*det, i12=(a02*a10-a12*a00)*det;
  const i20=b21*det,             i21=(a01*a20-a21*a00)*det, i22=(a11*a00-a01*a10)*det;
  const f00=from[0],f01=from[1],f02=from[2],
        f10=from[4],f11=from[5],f12=from[6],
        f20=from[8],f21=from[9],f22=from[10];
  out[0]=i00*f00+i01*f01+i02*f02; out[1]=i10*f00+i11*f01+i12*f02; out[2]=i20*f00+i21*f01+i22*f02; out[3]=0;
  out[4]=i00*f10+i01*f11+i02*f12; out[5]=i10*f10+i11*f11+i12*f12; out[6]=i20*f10+i21*f11+i22*f12; out[7]=0;
  out[8]=i00*f20+i01*f21+i02*f22; out[9]=i10*f20+i11*f21+i12*f22; out[10]=i20*f20+i21*f21+i22*f22; out[11]=0;
  const dx=from[12]-to[12], dy=from[13]-to[13], dz=from[14]-to[14];
  out[12]=i00*dx+i01*dy+i02*dz; out[13]=i10*dx+i11*dy+i12*dz; out[14]=i20*dx+i21*dy+i22*dz; out[15]=1;
  return out;
}

/**
 * Direction transform between frames: out = to₃ · inv(from₃).
 * Uses only the upper-left 3×3 blocks (rotation/scale, no translation).
 * @returns {ArrayLike<number>|null} out, or null if `from` is singular.
 */
function mat3Direction(out, from, to) {
  const a00=from[0],a01=from[1],a02=from[2],
        a10=from[4],a11=from[5],a12=from[6],
        a20=from[8],a21=from[9],a22=from[10];
  const b01=a22*a11-a12*a21, b11=a12*a20-a22*a10, b21=a21*a10-a11*a20;
  let det=a00*b01+a01*b11+a02*b21;
  if (Math.abs(det) < 1e-12) return null;
  det=1/det;
  const i00=b01*det,             i01=(a02*a21-a22*a01)*det, i02=(a12*a01-a02*a11)*det;
  const i10=b11*det,             i11=(a22*a00-a02*a20)*det, i12=(a02*a10-a12*a00)*det;
  const i20=b21*det,             i21=(a01*a20-a21*a00)*det, i22=(a11*a00-a01*a10)*det;
  const t00=to[0],t01=to[1],t02=to[2], t10=to[4],t11=to[5],t12=to[6], t20=to[8],t21=to[9],t22=to[10];
  out[0]=t00*i00+t10*i01+t20*i02; out[1]=t01*i00+t11*i01+t21*i02; out[2]=t02*i00+t12*i01+t22*i02;
  out[3]=t00*i10+t10*i11+t20*i12; out[4]=t01*i10+t11*i11+t21*i12; out[5]=t02*i10+t12*i11+t22*i12;
  out[6]=t00*i20+t10*i21+t20*i22; out[7]=t01*i20+t11*i21+t21*i22; out[8]=t02*i20+t12*i21+t22*i22;
  return out;
}

// ═══════════════════════════════════════════════════════════════════════════
// Space transforms — mapLocation / mapDirection
// ═══════════════════════════════════════════════════════════════════════════
//
// Flat dispatch: every from→to pair is a self-contained leaf with only stack
// locals — no reentrancy, no shared state between calls.
//
// Matrices bag `m`:
//   mat4Proj    Float32Array(16)  projection  (eye → clip)
//   mat4View    Float32Array(16)  view        (world → eye)
//   mat4Eye?    Float32Array(16)  eye         (eye → world); caller fills before passing
//   mat4PV?     Float32Array(16)  P · V;      caller fills or _ensurePV allocates once
//   mat4PVInv?  Float32Array(16)  inv(P · V); caller fills
//   fromFrame?  Float32Array(16)  MATRIX source frame
//   toFrameInv? Float32Array(16)  inv(MATRIX dest frame)
//
// Viewport `vp` = [x, y, w, h]:
//   Use SIGNED h to encode screen-y direction (see module header).
//   Core formula: screen = (ndc*0.5+0.5)*vp[k] + vp[k-2]  (k=2 for x, k=3 for y)
//   Inverse:      ndc    = ((screen-vp[k-2])/vp[k])*2 - 1
//   Negative vp[3] flips NDC y-up to screen y-down automatically.
//

// ── Location helpers ─────────────────────────────────────────────────────

function _worldToScreen(out, px, py, pz, pv, vp, ndcZMin) {
  const x=pv[0]*px+pv[4]*py+pv[8]*pz+pv[12], y=pv[1]*px+pv[5]*py+pv[9]*pz+pv[13],
        z=pv[2]*px+pv[6]*py+pv[10]*pz+pv[14], w=pv[3]*px+pv[7]*py+pv[11]*pz+pv[15];
  const xi=(w!==0&&w!==1)?1/w:1;
  out[0]=(x*xi*0.5+0.5)*vp[2]+vp[0];
  out[1]=(y*xi*0.5+0.5)*vp[3]+vp[1];
  out[2]=(z*xi-ndcZMin)/(1-ndcZMin);
  return out;
}

function _screenToWorld(out, sx, sy, sz, ipv, vp, ndcZMin) {
  return mat4MulPoint(out, ipv,
    ((sx-vp[0])/vp[2])*2-1,
    ((sy-vp[1])/vp[3])*2-1,
    sz*(1-ndcZMin)+ndcZMin);
}

function _worldToNDC(out, px, py, pz, pv) {
  const x=pv[0]*px+pv[4]*py+pv[8]*pz+pv[12], y=pv[1]*px+pv[5]*py+pv[9]*pz+pv[13],
        z=pv[2]*px+pv[6]*py+pv[10]*pz+pv[14], w=pv[3]*px+pv[7]*py+pv[11]*pz+pv[15];
  const xi=(w!==0&&w!==1)?1/w:1;
  out[0]=x*xi; out[1]=y*xi; out[2]=z*xi;
  return out;
}

function _ndcToWorld(out, nx, ny, nz, ipv) { return mat4MulPoint(out,ipv,nx,ny,nz); }

function _screenToNDC(out, sx, sy, sz, vp, ndcZMin) {
  out[0]=((sx-vp[0])/vp[2])*2-1;
  out[1]=((sy-vp[1])/vp[3])*2-1;
  out[2]=sz*(1-ndcZMin)+ndcZMin;
  return out;
}

function _ndcToScreen(out, nx, ny, nz, vp, ndcZMin) {
  out[0]=(nx*0.5+0.5)*vp[2]+vp[0];
  out[1]=(ny*0.5+0.5)*vp[3]+vp[1];
  out[2]=(nz-ndcZMin)/(1-ndcZMin);
  return out;
}

function _ensurePV(m) {
  if (m.mat4PV) return m.mat4PV;
  m.mat4PV = new Float32Array(16);
  mat4Mul(m.mat4PV, m.mat4Proj, m.mat4View);
  return m.mat4PV;
}

/**
 * Map a point between named coordinate spaces.
 *
 * @param {number[]} out     3-element destination — written and returned.
 * @param {number}   px,py,pz Input point.
 * @param {string}   from    Source space (WORLD, EYE, SCREEN, NDC, MATRIX).
 * @param {string}   to      Destination space.
 * @param {object}   m       Matrices bag — see module header.
 * @param {number[]} vp      Viewport [x, y, w, h]; sign of h encodes screen-y direction.
 * @param {number}   ndcZMin WEBGL (−1) or WEBGPU (0).
 * @returns {number[]} out
 */
function mapLocation(out, px, py, pz, from, to, m, vp, ndcZMin) {
  if (from===WORLD && to===SCREEN) return _worldToScreen(out,px,py,pz,_ensurePV(m),vp,ndcZMin);
  if (from===SCREEN && to===WORLD) return _screenToWorld(out,px,py,pz,m.mat4PVInv,vp,ndcZMin);

  if (from===WORLD && to===NDC)   return _worldToNDC(out,px,py,pz,_ensurePV(m));
  if (from===NDC   && to===WORLD) return _ndcToWorld(out,px,py,pz,m.mat4PVInv);

  if (from===SCREEN && to===NDC)    return _screenToNDC(out,px,py,pz,vp,ndcZMin);
  if (from===NDC    && to===SCREEN) return _ndcToScreen(out,px,py,pz,vp,ndcZMin);

  if (from===WORLD && to===EYE) return mat4MulPoint(out,m.mat4View,px,py,pz);
  if (from===EYE && to===WORLD) return mat4MulPoint(out,m.mat4Eye,px,py,pz);

  if (from===EYE && to===SCREEN) {
    const e=m.mat4Eye;
    return _worldToScreen(out,e[0]*px+e[4]*py+e[8]*pz+e[12],
                              e[1]*px+e[5]*py+e[9]*pz+e[13],
                              e[2]*px+e[6]*py+e[10]*pz+e[14],_ensurePV(m),vp,ndcZMin);
  }
  if (from===SCREEN && to===EYE) {
    _screenToWorld(out,px,py,pz,m.mat4PVInv,vp,ndcZMin);
    return mat4MulPoint(out,m.mat4View,out[0],out[1],out[2]);
  }

  if (from===EYE && to===NDC) {
    const e=m.mat4Eye;
    return _worldToNDC(out,e[0]*px+e[4]*py+e[8]*pz+e[12],
                           e[1]*px+e[5]*py+e[9]*pz+e[13],
                           e[2]*px+e[6]*py+e[10]*pz+e[14],_ensurePV(m));
  }
  if (from===NDC && to===EYE) {
    _ndcToWorld(out,px,py,pz,m.mat4PVInv);
    return mat4MulPoint(out,m.mat4View,out[0],out[1],out[2]);
  }

  if (from===MATRIX && to===WORLD) return mat4MulPoint(out,m.fromFrame,px,py,pz);
  if (from===WORLD && to===MATRIX) return mat4MulPoint(out,m.toFrameInv,px,py,pz);

  if (from===MATRIX && to===EYE) {
    const f=m.fromFrame;
    return mat4MulPoint(out,m.mat4View,f[0]*px+f[4]*py+f[8]*pz+f[12],
                                       f[1]*px+f[5]*py+f[9]*pz+f[13],
                                       f[2]*px+f[6]*py+f[10]*pz+f[14]);
  }
  if (from===EYE && to===MATRIX) {
    const e=m.mat4Eye;
    return mat4MulPoint(out,m.toFrameInv,e[0]*px+e[4]*py+e[8]*pz+e[12],
                                         e[1]*px+e[5]*py+e[9]*pz+e[13],
                                         e[2]*px+e[6]*py+e[10]*pz+e[14]);
  }

  if (from===MATRIX && to===SCREEN) {
    const f=m.fromFrame;
    return _worldToScreen(out,f[0]*px+f[4]*py+f[8]*pz+f[12],
                              f[1]*px+f[5]*py+f[9]*pz+f[13],
                              f[2]*px+f[6]*py+f[10]*pz+f[14],_ensurePV(m),vp,ndcZMin);
  }
  if (from===SCREEN && to===MATRIX) {
    _screenToWorld(out,px,py,pz,m.mat4PVInv,vp,ndcZMin);
    return mat4MulPoint(out,m.toFrameInv,out[0],out[1],out[2]);
  }

  if (from===MATRIX && to===NDC) {
    const f=m.fromFrame;
    return _worldToNDC(out,f[0]*px+f[4]*py+f[8]*pz+f[12],
                           f[1]*px+f[5]*py+f[9]*pz+f[13],
                           f[2]*px+f[6]*py+f[10]*pz+f[14],_ensurePV(m));
  }
  if (from===NDC && to===MATRIX) {
    _ndcToWorld(out,px,py,pz,m.mat4PVInv);
    return mat4MulPoint(out,m.toFrameInv,out[0],out[1],out[2]);
  }

  if (from===MATRIX && to===MATRIX) {
    const f=m.fromFrame;
    return mat4MulPoint(out,m.toFrameInv,f[0]*px+f[4]*py+f[8]*pz+f[12],
                                         f[1]*px+f[5]*py+f[9]*pz+f[13],
                                         f[2]*px+f[6]*py+f[10]*pz+f[14]);
  }

  out[0]=px; out[1]=py; out[2]=pz;
  return out;
}

// ── Direction helpers ────────────────────────────────────────────────────
//
// Directions use only the linear 3×3 block — no translation, no w-divide.
// The signed vp[2]/vp[3] carries the y-convention automatically.
//

function _applyDir(out, m, dx, dy, dz) {
  out[0]=m[0]*dx+m[4]*dy+m[8]*dz;
  out[1]=m[1]*dx+m[5]*dy+m[9]*dz;
  out[2]=m[2]*dx+m[6]*dy+m[10]*dz;
  return out;
}

function _worldToScreenDir(out, dx, dy, dz, proj, view, vpW, vpH, ndcZMin) {
  const vx=view[0]*dx+view[4]*dy+view[8]*dz,
        vy=view[1]*dx+view[5]*dy+view[9]*dz,
        vz=view[2]*dx+view[6]*dy+view[10]*dz;
  // vpH is signed — negative flips y component automatically.
  out[0]=(proj[0]*vx+proj[4]*vy+proj[8]*vz)*vpW*0.5;
  out[1]=(proj[1]*vx+proj[5]*vy+proj[9]*vz)*vpH*0.5;
  out[2]=(proj[2]*vx+proj[6]*vy+proj[10]*vz)*(1-ndcZMin)*0.5;
  return out;
}

function _screenToWorldDir(out, dx, dy, dz, proj, eye, vpW, vpH, ndcZMin) {
  // Inverse of _worldToScreenDir; signed vpW/vpH cancel the y-flip.
  _applyDir(out, eye, dx/(vpW*0.5)/proj[0], dy/(vpH*0.5)/proj[5], dz/((1-ndcZMin)*0.5));
  return out;
}

function _screenToNDCDir(out, dx, dy, dz, vpW, vpH, ndcZMin) {
  out[0]=dx/(vpW*0.5); out[1]=dy/(vpH*0.5); out[2]=dz/((1-ndcZMin)*0.5);
  return out;
}

function _ndcToScreenDir(out, dx, dy, dz, vpW, vpH, ndcZMin) {
  out[0]=dx*vpW*0.5; out[1]=dy*vpH*0.5; out[2]=dz*(1-ndcZMin)*0.5;
  return out;
}

/**
 * Map a direction between named coordinate spaces.
 * Same bag and viewport contract as mapLocation.
 *
 * @param {number[]} out     3-element destination.
 * @param {number}   dx,dy,dz Input direction.
 * @param {string}   from    Source space.
 * @param {string}   to      Destination space.
 * @param {object}   m       Matrices bag — see module header.
 * @param {number[]} vp      Viewport [x, y, w, h]; sign of h encodes screen-y direction.
 * @param {number}   ndcZMin WEBGL (−1) or WEBGPU (0).
 * @returns {number[]} out
 */
function mapDirection(out, dx, dy, dz, from, to, m, vp, ndcZMin) {
  const vpW=vp[2], vpH=vp[3];  // signed — carry y-convention through all helpers

  if (from===EYE   && to===WORLD)  return _applyDir(out,m.mat4Eye, dx,dy,dz);
  if (from===WORLD && to===EYE)    return _applyDir(out,m.mat4View,dx,dy,dz);

  if (from===WORLD  && to===SCREEN) return _worldToScreenDir(out,dx,dy,dz,m.mat4Proj,m.mat4View,vpW,vpH,ndcZMin);
  if (from===SCREEN && to===WORLD)  return _screenToWorldDir(out,dx,dy,dz,m.mat4Proj,m.mat4Eye, vpW,vpH,ndcZMin);

  if (from===SCREEN && to===NDC)    return _screenToNDCDir(out,dx,dy,dz,vpW,vpH,ndcZMin);
  if (from===NDC    && to===SCREEN) return _ndcToScreenDir(out,dx,dy,dz,vpW,vpH,ndcZMin);

  if (from===WORLD && to===NDC) {
    _worldToScreenDir(out,dx,dy,dz,m.mat4Proj,m.mat4View,vpW,vpH,ndcZMin);
    return _screenToNDCDir(out,out[0],out[1],out[2],vpW,vpH,ndcZMin);
  }
  if (from===NDC && to===WORLD) {
    _ndcToScreenDir(out,dx,dy,dz,vpW,vpH,ndcZMin);
    return _screenToWorldDir(out,out[0],out[1],out[2],m.mat4Proj,m.mat4Eye,vpW,vpH,ndcZMin);
  }

  if (from===EYE && to===SCREEN) {
    _applyDir(out,m.mat4Eye,dx,dy,dz);
    return _worldToScreenDir(out,out[0],out[1],out[2],m.mat4Proj,m.mat4View,vpW,vpH,ndcZMin);
  }
  if (from===SCREEN && to===EYE) {
    _screenToWorldDir(out,dx,dy,dz,m.mat4Proj,m.mat4Eye,vpW,vpH,ndcZMin);
    return _applyDir(out,m.mat4View,out[0],out[1],out[2]);
  }

  if (from===EYE && to===NDC) {
    _applyDir(out,m.mat4Eye,dx,dy,dz);
    _worldToScreenDir(out,out[0],out[1],out[2],m.mat4Proj,m.mat4View,vpW,vpH,ndcZMin);
    return _screenToNDCDir(out,out[0],out[1],out[2],vpW,vpH,ndcZMin);
  }
  if (from===NDC && to===EYE) {
    _ndcToScreenDir(out,dx,dy,dz,vpW,vpH,ndcZMin);
    _screenToWorldDir(out,out[0],out[1],out[2],m.mat4Proj,m.mat4Eye,vpW,vpH,ndcZMin);
    return _applyDir(out,m.mat4View,out[0],out[1],out[2]);
  }

  if (from===MATRIX && to===WORLD)  return _applyDir(out,m.fromFrame, dx,dy,dz);
  if (from===WORLD  && to===MATRIX) return _applyDir(out,m.toFrameInv,dx,dy,dz);

  if (from===MATRIX && to===EYE) {
    _applyDir(out,m.fromFrame,dx,dy,dz);
    return _applyDir(out,m.mat4View,out[0],out[1],out[2]);
  }
  if (from===EYE && to===MATRIX) {
    _applyDir(out,m.mat4Eye,dx,dy,dz);
    return _applyDir(out,m.toFrameInv,out[0],out[1],out[2]);
  }

  if (from===MATRIX && to===SCREEN) {
    _applyDir(out,m.fromFrame,dx,dy,dz);
    return _worldToScreenDir(out,out[0],out[1],out[2],m.mat4Proj,m.mat4View,vpW,vpH,ndcZMin);
  }
  if (from===SCREEN && to===MATRIX) {
    _screenToWorldDir(out,dx,dy,dz,m.mat4Proj,m.mat4Eye,vpW,vpH,ndcZMin);
    return _applyDir(out,m.toFrameInv,out[0],out[1],out[2]);
  }

  if (from===MATRIX && to===NDC) {
    _applyDir(out,m.fromFrame,dx,dy,dz);
    _worldToScreenDir(out,out[0],out[1],out[2],m.mat4Proj,m.mat4View,vpW,vpH,ndcZMin);
    return _screenToNDCDir(out,out[0],out[1],out[2],vpW,vpH,ndcZMin);
  }
  if (from===NDC && to===MATRIX) {
    _ndcToScreenDir(out,dx,dy,dz,vpW,vpH,ndcZMin);
    _screenToWorldDir(out,out[0],out[1],out[2],m.mat4Proj,m.mat4Eye,vpW,vpH,ndcZMin);
    return _applyDir(out,m.toFrameInv,out[0],out[1],out[2]);
  }

  if (from===MATRIX && to===MATRIX) {
    _applyDir(out,m.fromFrame,dx,dy,dz);
    return _applyDir(out,m.toFrameInv,out[0],out[1],out[2]);
  }

  out[0]=dx; out[1]=dy; out[2]=dz;
  return out;
}

// ═══════════════════════════════════════════════════════════════════════════
// pixelRatio
// ═══════════════════════════════════════════════════════════════════════════

/**
 * World-units-per-pixel at a given eye-space Z depth.
 * @param {ArrayLike<number>} proj    Projection mat4.
 * @param {number}            vpH     Viewport height in pixels (positive).
 * @param {number}            eyeZ    Eye-space Z — negative means in front of camera.
 * @param {number}            ndcZMin WEBGL (−1) or WEBGPU (0).
 */
function pixelRatio(proj, vpH, eyeZ, ndcZMin) {
  return projIsOrtho(proj)
    ? Math.abs(projTop(proj,ndcZMin)-projBottom(proj,ndcZMin)) / vpH
    : 2*Math.abs(eyeZ)*Math.tan(projFov(proj)/2) / vpH;
}

// ═══════════════════════════════════════════════════════════════════════════
// Pick-matrix
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Mutate a projection matrix in-place so that the pixel at (px, py) maps to
 * the full NDC square — making a 1×1 FBO render contain exactly that pixel.
 *
 * Premultiplies by M_pick (column-major, rows 2 and 3 unchanged):
 *
 *   ┌  sx   0   0   tx ┐     sx = |vp[2]|,   sy = |vp[3]|
 *   │   0  sy   0   ty │     cx = ((px−vp[0])/vp[2])·2 − 1   (NDC x of pixel centre)
 *   │   0   0   1    0 │     cy = ((py−vp[1])/vp[3])·2 − 1   (NDC y, sign-aware)
 *   └   0   0   0    1 ┘     tx = −cx·sx,  ty = −cy·sy
 *
 * Result: P_pick = M_pick · P_original.
 * The viewport sign convention (vp[3] < 0 for screen y-down) is preserved
 * automatically through cx/cy — no separate flip needed.
 *
 * @param {Float32Array} proj  Projection mat4 — mutated in place.
 * @param {number} px  Query pixel X in screen coordinates.
 * @param {number} py  Query pixel Y in screen coordinates.
 * @param {number[]} vp  Viewport [x, y, w, h]; same signed convention as mapLocation.
 */
function mat4Pick(proj, px, py, vp) {
  const cx=((px-vp[0])/vp[2])*2-1;
  const cy=((py-vp[1])/vp[3])*2-1;
  const sx=Math.abs(vp[2]), sy=Math.abs(vp[3]);
  const tx=-cx*sx, ty=-cy*sy;
  for (let j=0; j<4; j++) {
    const a=proj[j*4], b=proj[j*4+1], d=proj[j*4+3];
    proj[j*4]   = sx*a + tx*d;
    proj[j*4+1] = sy*b + ty*d;
  }
}

// =========================================================================
// Decomposition
// =========================================================================

/**
 * Extract translation from a column-major mat4 (column 3).
 * @param {Float32Array|number[]} out3  3-element destination.
 * @param {Float32Array|number[]} m     16-element source.
 */
function mat4ToTranslation(out3, m) {
  out3[0]=m[12]; out3[1]=m[13]; out3[2]=m[14];
  return out3;
}

/**
 * Extract scale from a column-major mat4 (column lengths of the rotation block).
 * Assumes no shear.
 * @param {Float32Array|number[]} out3  3-element destination.
 * @param {Float32Array|number[]} m     16-element source.
 */
function mat4ToScale(out3, m) {
  out3[0]=Math.sqrt(m[0]*m[0]+m[1]*m[1]+m[2]*m[2]);
  out3[1]=Math.sqrt(m[4]*m[4]+m[5]*m[5]+m[6]*m[6]);
  out3[2]=Math.sqrt(m[8]*m[8]+m[9]*m[9]+m[10]*m[10]);
  return out3;
}

/**
 * Extract rotation as a unit quaternion from a column-major mat4.
 * Scale is factored out from each column. Assumes no shear.
 * @param {number[]} out4  4-element [x,y,z,w] destination.
 * @param {Float32Array|number[]} m  16-element source.
 */
function mat4ToRotation(out4, m) {
  const sx=Math.sqrt(m[0]*m[0]+m[1]*m[1]+m[2]*m[2])||1;
  const sy=Math.sqrt(m[4]*m[4]+m[5]*m[5]+m[6]*m[6])||1;
  const sz=Math.sqrt(m[8]*m[8]+m[9]*m[9]+m[10]*m[10])||1;
  return qFromRotMat3x3(out4,
    m[0]/sx, m[4]/sy, m[8]/sz,
    m[1]/sx, m[5]/sy, m[9]/sz,
    m[2]/sx, m[6]/sy, m[10]/sz);
}

/**
 * @file Spline math and keyframe animation state machines.
 * @module tree/track
 * @license AGPL-3.0-only
 *
 * Quaternion algebra is provided by quat.js — this module imports and uses
 * it but does not define it. Spline helpers (hermiteVec3, lerpVec3) and
 * TRS↔mat4 conversions (transformToMat4, mat4ToTransform) remain here
 * because they are tightly coupled to the PoseTrack keyframe shape.
 *
 * Zero dependencies on p5, DOM, WebGL, or WebGPU.
 *
 * ── Exports ──────────────────────────────────────────────────────────────────
 *  Quaternion helpers  (re-exported from quat.js)
 *    qSet qCopy qDot qNormalize qNegate qMul qSlerp qNlerp
 *    qFromAxisAngle qFromLookDir qFromRotMat3x3 qFromMat4 qToMat4
 *    quatToAxisAngle
 *  Spline / vector helpers
 *    hermiteVec3  lerpVec3
 *  Transform / mat4 helpers
 *    transformToMat4  mat4ToTransform
 *  Tracks
 *    PoseTrack    — { pos, rot, scl } TRS keyframes
 *    CameraTrack  — { eye, center, up } lookat keyframes
 *
 * ── Class hierarchy ───────────────────────────────────────────────────────────
 *  Track (unexported, never instantiated directly)
 *    └── PoseTrack   (exported)
 *    └── CameraTrack (exported)
 *
 *  Track holds all transport machinery: cursor, play/stop/seek/tick,
 *  hooks, rate semantics.  Subclasses add only keyframe storage and
 *  add() / eval() for their respective data shape.
 *
 * ── Hook architecture ─────────────────────────────────────────────────────────
 *  Lib-space hooks (underscore prefix — reserved for host layer / UI layer):
 *    _onActivate / _onDeactivate  — fire on playing transitions false→true / true→false.
 *    _onPlay / _onEnd / _onStop   — mirror the user-space hooks; used by the UI layer
 *                                   so it can sync without chaining the public slots.
 *
 *  User-space hooks (public):
 *    onPlay : fires in play()  on false→true transition.
 *    onEnd  : fires in tick()  at natural boundary (once mode only).
 *    onStop : fires in stop() / reset() — explicit deactivation.
 *    onEnd and onStop are mutually exclusive per event.
 *
 *  Firing order:
 *    play()  → onPlay → _onPlay → _onActivate
 *    tick()  → onEnd  → _onEnd  → _onDeactivate
 *    stop()  → onStop → _onStop → _onDeactivate
 *    reset() → onStop → _onStop → _onDeactivate
 *
 * ── Loop modes ────────────────────────────────────────────────────────────────
 *  loop:false, bounce:false  — play once, stop at end (fires onEnd)
 *  loop:true,  bounce:false  — repeat, wrap back to start
 *  loop:true,  bounce:true   — bounce forever at boundaries
 *  loop:false, bounce:true   — bounce once: flip at far boundary, stop at origin
 *
 *  bounce and loop are fully independent flags — no exclusivity enforced.
 *
 * ── Playback semantics (rate + _dir) ─────────────────────────────────────────
 *  rate > 0   forward
 *  rate < 0   backward
 *  rate === 0 frozen: tick() no-op; playing unchanged
 *
 *  play() is the sole setter of playing = true.
 *  stop() is the sole setter of playing = false.
 *  Assigning rate never starts or stops playback.
 *
 *  _dir (internal, ±1) tracks the current bounce travel direction.
 *  tick() advances by rate * _dir and flips _dir at boundaries.
 *  rate always holds the user-set value — it is never mutated by bounce.
 *  _dir is reset to 1 only in reset() (keyframes cleared) — stop/replay
 *  preserves the current travel direction.
 *
 * ── One-keyframe behaviour ────────────────────────────────────────────────────
 *  play() with exactly one keyframe snaps eval() to that keyframe without
 *  setting playing = true and without firing hooks.
 */


// =========================================================================
// S2  Spline / vector helpers
// =========================================================================

function _dist3(a, b) {
  const dx=a[0]-b[0], dy=a[1]-b[1], dz=a[2]-b[2];
  return Math.sqrt(dx*dx+dy*dy+dz*dz);
}

/**
 * Cubic Hermite interpolation between p0 and p1 with explicit tangents.
 * Catmull-Rom is a special case where m0/m1 are auto-computed from neighbors.
 * @param {number[]} out  3-element result.
 * @param {number[]} p0   Segment start.
 * @param {number[]} m0   Outgoing tangent at p0 (world-space, dp/dt scaled to segment).
 * @param {number[]} p1   Segment end.
 * @param {number[]} m1   Incoming tangent at p1 (world-space, dp/dt scaled to segment).
 * @param {number}   t    Blend [0, 1].
 * @returns {number[]} out
 */
const hermiteVec3 = (out, p0, m0, p1, m1, t) => {
  const t2=t*t, t3=t2*t;
  const h00=2*t3-3*t2+1, h10=t3-2*t2+t, h01=-2*t3+3*t2, h11=t3-t2;
  out[0]=h00*p0[0]+h10*m0[0]+h01*p1[0]+h11*m1[0];
  out[1]=h00*p0[1]+h10*m0[1]+h01*p1[1]+h11*m1[1];
  out[2]=h00*p0[2]+h10*m0[2]+h01*p1[2]+h11*m1[2];
  return out;
};

// Centripetal CR outgoing tangent at p1 for segment p1→p2, scaled by dt1.
const _crTanOut = (out, p0, p1, p2) => {
  const dt0=Math.pow(_dist3(p0,p1),0.5)||1, dt1=Math.pow(_dist3(p1,p2),0.5)||1;
  for (let i=0;i<3;i++) out[i]=((p1[i]-p0[i])/dt0-(p2[i]-p0[i])/(dt0+dt1)+(p2[i]-p1[i])/dt1)*dt1;
  return out;
};

const _crTanIn = (out, p1, p2, p3) => {
  const dt1=Math.pow(_dist3(p1,p2),0.5)||1, dt2=Math.pow(_dist3(p2,p3),0.5)||1;
  for (let i=0;i<3;i++) out[i]=((p2[i]-p1[i])/dt1-(p3[i]-p1[i])/(dt1+dt2)+(p3[i]-p2[i])/dt2)*dt1;
  return out;
};

// Module-level scratch — shared by eval() across all track instances (non-reentrant hot path).
const _m0=[0,0,0], _m1=[0,0,0];

/**
 * Linear interpolation between two vec3s.
 * @param {number[]} out
 * @param {number[]} a
 * @param {number[]} b
 * @param {number}   t  Blend [0, 1].
 * @returns {number[]} out
 */
const lerpVec3 = (out, a, b, t) => {
  out[0]=a[0]+t*(b[0]-a[0]);
  out[1]=a[1]+t*(b[1]-a[1]);
  out[2]=a[2]+t*(b[2]-a[2]);
  return out;
};

// =========================================================================
// S3  Transform <-> Mat4
// =========================================================================

/**
 * Write a TRS transform into a column-major mat4.
 * @param {Float32Array|number[]} out  16-element column-major mat4.
 * @param {{ pos:number[], rot:number[], scl:number[] }} xform
 * @returns {Float32Array|number[]} out
 */
const transformToMat4 = (out, xform) => {
  qToMat4(out, xform.rot);
  const sx=xform.scl[0], sy=xform.scl[1], sz=xform.scl[2];
  out[0]*=sx; out[1]*=sx; out[2]*=sx;
  out[4]*=sy; out[5]*=sy; out[6]*=sy;
  out[8]*=sz; out[9]*=sz; out[10]*=sz;
  out[12]=xform.pos[0]; out[13]=xform.pos[1]; out[14]=xform.pos[2];
  return out;
};

/**
 * Decompose a column-major mat4 into a TRS transform.
 * Assumes no shear. Scale extracted from column lengths.
 * @param {{ pos:number[], rot:number[], scl:number[] }} out
 * @param {Float32Array|number[]} m  Column-major mat4.
 * @returns {{ pos:number[], rot:number[], scl:number[] }} out
 */
const mat4ToTransform = (out, m) => {
  out.pos[0]=m[12]; out.pos[1]=m[13]; out.pos[2]=m[14];
  const sx=Math.sqrt(m[0]*m[0]+m[1]*m[1]+m[2]*m[2]);
  const sy=Math.sqrt(m[4]*m[4]+m[5]*m[5]+m[6]*m[6]);
  const sz=Math.sqrt(m[8]*m[8]+m[9]*m[9]+m[10]*m[10]);
  out.scl[0]=sx; out.scl[1]=sy; out.scl[2]=sz;
  qFromRotMat3x3(out.rot,
    m[0]/sx,m[4]/sy,m[8]/sz,
    m[1]/sx,m[5]/sy,m[9]/sz,
    m[2]/sx,m[6]/sy,m[10]/sz);
  return out;
};

// =========================================================================
// S4a  Spec parser — PoseTrack
// =========================================================================

const _isNum   = (x) => typeof x === 'number' && Number.isFinite(x);
const _clamp01 = (x) => x < 0 ? 0 : (x > 1 ? 1 : x);
const _clampS  = (x, lo, hi) => x < lo ? lo : (x > hi ? hi : x);

function _parseVec3(v) {
  if (!v) return null;
  if (ArrayBuffer.isView(v) && v.length >= 3) return [v[0], v[1], v[2]];
  if (Array.isArray(v) && v.length >= 3 && v.every(n => typeof n === 'number')) return [v[0], v[1], v[2]];
  if (typeof v === 'object' && 'x' in v) return [v.x || 0, v.y || 0, v.z || 0];
  return null;
}

// Euler: unit axis vectors and the six valid intrinsic orderings.
const _EULER_AXES   = { X:[1,0,0], Y:[0,1,0], Z:[0,0,1] };
const _EULER_ORDERS = new Set(['XYZ','XZY','YXZ','YZX','ZXY','ZYX']);

/**
 * Parse any rotation representation into a unit quaternion [x,y,z,w].
 *
 * Accepted forms:
 *
 *   [x,y,z,w]                        — raw quaternion array
 *   { axis:[x,y,z], angle }          — axis-angle
 *   { dir:[x,y,z], up?:[x,y,z] }     — forward direction (−Z) with optional up
 *   { mat4Eye: mat4 }                — rotation block of an eye matrix
 *   { mat3: mat3 }                   — column-major 3×3 rotation matrix
 *   { euler:[rx,ry,rz], order? }     — intrinsic Euler (default order: YXZ)
 *   { from:[x,y,z], to:[x,y,z] }     — shortest-arc rotation
 *
 * @param {*} v
 * @returns {number[]|null}  [x,y,z,w] or null if unparseable.
 */
function _parseQuat(v) {
  if (!v) return null;

  // Raw array [x,y,z,w]
  if (Array.isArray(v) && v.length === 4) return [v[0],v[1],v[2],v[3]];
  if (ArrayBuffer.isView(v) && v.length >= 4) return [v[0],v[1],v[2],v[3]];

  if (typeof v !== 'object') return null;

  // { axis, angle }
  if (v.axis != null && v.angle != null) {
    const ax = Array.isArray(v.axis) ? v.axis : [v.axis.x||0, v.axis.y||0, v.axis.z||0];
    return qFromAxisAngle([0,0,0,1], ax[0],ax[1],ax[2], v.angle);
  }

  // { dir, up? }
  if (v.dir != null) {
    const d = Array.isArray(v.dir) ? v.dir : [v.dir.x||0, v.dir.y||0, v.dir.z||0];
    const u = v.up ? (Array.isArray(v.up) ? v.up : [v.up.x||0, v.up.y||0, v.up.z||0]) : null;
    return qFromLookDir([0,0,0,1], d, u);
  }

  // { mat4Eye }
  if (v.mat4Eye != null) {
    const m = (ArrayBuffer.isView(v.mat4Eye) || Array.isArray(v.mat4Eye))
      ? v.mat4Eye : (v.mat4Eye.mat4 ?? null);
    if (!m || m.length < 16) return null;
    return qFromRotMat3x3([0,0,0,1], m[0],m[4],m[8], m[1],m[5],m[9], m[2],m[6],m[10]);
  }

  // { mat3 }
  if (v.mat3 != null) {
    const m = (ArrayBuffer.isView(v.mat3) || Array.isArray(v.mat3))
      ? v.mat3 : null;
    if (!m || m.length < 9) return null;
    return qFromRotMat3x3([0,0,0,1], m[0],m[3],m[6], m[1],m[4],m[7], m[2],m[5],m[8]);
  }

  // { euler, order? }
  if (v.euler != null) {
    const e = v.euler;
    if (!Array.isArray(e) || e.length < 3) return null;
    const order = (v.order && _EULER_ORDERS.has(v.order)) ? v.order : 'YXZ';
    const q = [0,0,0,1];
    const s = [0,0,0,1];
    for (let i = 0; i < 3; i++) {
      const ax = _EULER_AXES[order[i]];
      qMul(q, q, qFromAxisAngle(s, ax[0],ax[1],ax[2], e[i]));
    }
    return q;
  }

  // { from, to }
  if (v.from != null && v.to != null) {
    const f = Array.isArray(v.from) ? v.from : [v.from.x||0, v.from.y||0, v.from.z||0];
    const t = Array.isArray(v.to)   ? v.to   : [v.to.x||0,   v.to.y||0,   v.to.z||0];
    const fl = Math.sqrt(f[0]*f[0]+f[1]*f[1]+f[2]*f[2]) || 1;
    const tl = Math.sqrt(t[0]*t[0]+t[1]*t[1]+t[2]*t[2]) || 1;
    const fx=f[0]/fl, fy=f[1]/fl, fz=f[2]/fl;
    const tx=t[0]/tl, ty=t[1]/tl, tz=t[2]/tl;
    const dot = fx*tx + fy*ty + fz*tz;
    if (dot >= 1 - 1e-8) return [0,0,0,1];
    if (dot <= -1 + 1e-8) {
      let px=0, py=fz, pz=-fy;
      let pl = Math.sqrt(px*px+py*py+pz*pz);
      if (pl < 1e-8) { px=fy; py=-fx; pz=0; pl = Math.sqrt(px*px+py*py+pz*pz); }
      if (pl < 1e-8) return [0,0,0,1];
      return qFromAxisAngle([0,0,0,1], px/pl,py/pl,pz/pl, Math.PI);
    }
    let ax=fy*tz-fz*ty, ay=fz*tx-fx*tz, az=fx*ty-fy*tx;
    const al = Math.sqrt(ax*ax+ay*ay+az*az) || 1;
    return qFromAxisAngle([0,0,0,1], ax/al,ay/al,az/al,
      Math.acos(Math.max(-1, Math.min(1, dot))));
  }

  return null;
}

/**
 * Parse a PoseTrack keyframe spec.
 *
 * Accepted forms:
 *
 *   { mat4Model }
 *     Decompose a column-major mat4 into TRS via mat4ToTransform.
 *     Float32Array(16), plain Array, or { mat4 } wrapper.
 *
 *   { pos?, rot?, scl?, tanIn?, tanOut? }
 *     Explicit TRS.  pos and scl are vec3, rot accepts any form from _parseQuat.
 *     All fields are optional — missing pos/scl default to [0,0,0] / [1,1,1],
 *     missing rot defaults to identity.
 *     tanIn/tanOut are optional vec3 tangents for Hermite interpolation.
 *
 * @param {Object} spec
 * @returns {{ pos:number[], rot:number[], scl:number[], tanIn:number[]|null, tanOut:number[]|null }|null}
 */
function _parseSpec(spec) {
  if (!spec || typeof spec !== 'object') return null;

  // { mat4Model } — full TRS decomposition from model matrix
  if (spec.mat4Model != null) {
    const m = (ArrayBuffer.isView(spec.mat4Model) || Array.isArray(spec.mat4Model))
      ? spec.mat4Model : (spec.mat4Model.mat4 ?? null);
    if (!m || m.length < 16) return null;
    const kf = mat4ToTransform({ pos:[0,0,0], rot:[0,0,0,1], scl:[1,1,1] }, m);
    kf.tanIn = null; kf.tanOut = null;
    return kf;
  }

  const pos    = _parseVec3(spec.pos)    || [0,0,0];
  const rot    = _parseQuat(spec.rot)    || [0,0,0,1];
  const scl    = _parseVec3(spec.scl)    || [1,1,1];
  const tanIn  = _parseVec3(spec.tanIn)  || null;
  const tanOut = _parseVec3(spec.tanOut) || null;
  return { pos, rot, scl, tanIn, tanOut };
}

function _sameTransform(a, b) {
  for (let i=0;i<3;i++) if (a.pos[i]!==b.pos[i]||a.scl[i]!==b.scl[i]) return false;
  for (let i=0;i<4;i++) if (a.rot[i]!==b.rot[i]) return false;
  return true;
}

// =========================================================================
// S4b  Spec parser — CameraTrack
// =========================================================================

/**
 * Parse a camera keyframe spec into internal { eye, center, up } form.
 *
 * Accepted forms:
 *
 *   { eye, center?, up?, fov?, halfHeight?,
 *     eyeTanIn?, eyeTanOut?, centerTanIn?, centerTanOut? }
 *     Explicit lookat.  center defaults to [0,0,0], up defaults to [0,1,0].
 *     eyeTanIn/Out and centerTanIn/Out are optional vec3 tangents for Hermite.
 *     When absent, centripetal Catmull-Rom tangents are auto-computed at eval time.
 *
 *   Removed forms (task 2):
 *     { mat4View } and { mat4Eye } — use PoseTrack.add({ mat4Model: mat4Eye }) for
 *     full-fidelity capture including roll, or cam.capturePose() for lookat-style.
 *
 * @param {Object} spec
 * @returns {{ eye:number[], center:number[], up:number[],
 *             fov:number|null, halfHeight:number|null,
 *             eyeTanIn:number[]|null, eyeTanOut:number[]|null,
 *             centerTanIn:number[]|null, centerTanOut:number[]|null }|null}
 */
function _parseCameraSpec(spec) {
  if (!spec || typeof spec !== 'object') return null;

  // { eye, center?, up? } — explicit lookat
  const eye = _parseVec3(spec.eye);
  if (!eye) return null;
  const center = _parseVec3(spec.center) || [0,0,0];
  const upRaw  = spec.up ? _parseVec3(spec.up) : null;
  const up     = upRaw || [0,1,0];
  const ul     = Math.sqrt(up[0]*up[0]+up[1]*up[1]+up[2]*up[2]) || 1;
  return {
    eye, center,
    up: [up[0]/ul, up[1]/ul, up[2]/ul],
    fov:         typeof spec.fov        === 'number' ? spec.fov        : null,
    halfHeight:  typeof spec.halfHeight === 'number' ? spec.halfHeight : null,
    eyeTanIn:    _parseVec3(spec.eyeTanIn)    || null,
    eyeTanOut:   _parseVec3(spec.eyeTanOut)   || null,
    centerTanIn: _parseVec3(spec.centerTanIn) || null,
    centerTanOut:_parseVec3(spec.centerTanOut)|| null,
  };
}

function _sameCameraKeyframe(a, b) {
  for (let i=0;i<3;i++) {
    if (a.eye[i]!==b.eye[i]) return false;
    if (a.center[i]!==b.center[i]) return false;
    if (a.up[i]!==b.up[i]) return false;
  }
  if (a.fov !== b.fov) return false;
  if (a.halfHeight !== b.halfHeight) return false;
  return true;
}

// =========================================================================
// S5  Track — unexported base class (transport machinery only)
// =========================================================================

class Track {
  constructor() {
    /** @type {Array} Keyframe array — shape depends on subclass. */
    this.keyframes = [];
    /** Whether playback is active. @type {boolean} */
    this.playing   = false;
    /** Loop at boundaries. @type {boolean} */
    this.loop      = false;
    /** Ping-pong bounce (takes precedence over loop). @type {boolean} */
    this.bounce    = false;
    /** Frames per segment (≥1). @type {number} */
    this.duration  = 30;
    /** Current segment index. @type {number} */
    this.seg       = 0;
    /** Frame offset within segment (can be fractional). @type {number} */
    this.f         = 0;

    // Internal rate — never directly starts/stops playback
    this._rate = 1;
    // Internal bounce direction: +1 forward, -1 backward.
    this._dir  = 1;
    // Scratch: true once _dir has been flipped in bounce-once mode.
    this._bounced = false;

    // User-space hooks
    /** @type {Function|null} */ this.onPlay = null;
    /** @type {Function|null} */ this.onEnd  = null;
    /** @type {Function|null} */ this.onStop = null;

    // Lib-space hooks (set by host layer, e.g. p5 bridge)
    /** @type {Function|null} */ this._onActivate   = null;
    /** @type {Function|null} */ this._onDeactivate = null;
    // Lib-space event mirrors — set by UI layer (trackUI), never touched by user code
    /** @type {Function|null} */ this._onPlay = null;
    /** @type {Function|null} */ this._onEnd  = null;
    /** @type {Function|null} */ this._onStop = null;
  }

  /** Playback rate. Assigning never starts/stops playback. @type {number} */
  get rate()  { return this._rate; }
  set rate(v) { this._rate = (_isNum(v)) ? v : 1; }

  /** Number of interpolatable segments (keyframes.length − 1, min 0). @type {number} */
  get segments() { return Math.max(0, this.keyframes.length - 1); }

  /**
   * Start or update playback.
   * @param {number|Object} [rateOrOpts]  Numeric rate or options object:
   *   { rate, duration, loop, bounce, onPlay, onEnd, onStop }
   * @returns {Track} this
   */
  play(rateOrOpts) {
    if (this.keyframes.length === 0) return this;

    // One keyframe: snap cursor, no animation
    if (this.keyframes.length === 1) {
      this.seg = 0; this.f = 0;
      return this;
    }

    if (typeof rateOrOpts === 'number' && Number.isFinite(rateOrOpts)) {
      this._rate = rateOrOpts;
    } else if (rateOrOpts && typeof rateOrOpts === 'object') {
      const o = rateOrOpts;
      if (_isNum(o.duration))             this.duration  = Math.max(1, o.duration | 0);
      if ('loop'   in o) this.loop   = !!o.loop;
      if ('bounce' in o) this.bounce = !!o.bounce;
      if (typeof o.onPlay === 'function') this.onPlay   = o.onPlay;
      if (typeof o.onEnd  === 'function') this.onEnd    = o.onEnd;
      if (typeof o.onStop === 'function') this.onStop   = o.onStop;
      if (_isNum(o.rate))                 this._rate    = o.rate;
    }

    const nSeg = this.segments, dur = Math.max(1, this.duration | 0);
    if (this.seg < 0)     this.seg = 0;
    if (this.seg >= nSeg) this.seg = nSeg - 1;
    if (this.f   < 0)     this.f   = 0;
    if (this.f   > dur)   this.f   = dur;

    const wasPlaying = this.playing;
    this.playing = true;
    if (!wasPlaying) {
      this._bounced = false;
      if (typeof this.onPlay === 'function') { try { this.onPlay(this); } catch (_) {} }
      this._onPlay?.();
      this._onActivate?.();
    }
    return this;
  }

  /**
   * Stop playback.
   * @param {boolean} [rewind=false]  Seek to origin after stopping.
   * @returns {Track} this
   */
  stop(rewind) {
    const wasPlaying = this.playing;
    this.playing = false;
    if (wasPlaying) {
      this._bounced = false;
      if (typeof this.onStop === 'function') { try { this.onStop(this); } catch (_) {} }
      this._onStop?.();
      this._onDeactivate?.();
      if (rewind && this.keyframes.length > 1) this.seek(this._rate * this._dir < 0 ? 1 : 0);
    }
    return this;
  }

  /**
   * Clear all keyframes and stop.
   * @returns {Track} this
   */
  reset() {
    const wasPlaying = this.playing;
    this.playing = false;
    if (wasPlaying) {
      if (typeof this.onStop === 'function') { try { this.onStop(this); } catch (_) {} }
      this._onStop?.();
      this._onDeactivate?.();
    }
    this.keyframes.length = 0;
    this.seg = 0; this.f = 0; this._dir = 1; this._bounced = false;
    return this;
  }

  /**
   * Remove the keyframe at index. Adjusts cursor if needed.
   * @param {number} index
   * @returns {boolean}
   */
  remove(index) {
    if (!_isNum(index)) return false;
    const i = index | 0;
    if (i < 0 || i >= this.keyframes.length) return false;
    this.keyframes.splice(i, 1);
    const nSeg = this.segments;
    if (nSeg === 0) { this.seg = 0; this.f = 0; }
    else if (this.seg >= nSeg) { this.seg = nSeg - 1; }
    return true;
  }

  /**
   * Seek to a normalised position [0,1] across the full path.
   * @param {number} t           Normalised time [0, 1].
   * @param {number} [segIndex]  Optional segment override.
   * @returns {Track} this
   */
  seek(t, segIndex) {
    const nSeg = this.segments;
    if (nSeg === 0) { this.seg = 0; this.f = 0; return this; }
    const dur = Math.max(1, this.duration | 0);
    if (_isNum(segIndex)) {
      this.seg = _clampS(segIndex | 0, 0, nSeg - 1);
      this.f   = _clamp01(t) * dur;
    } else {
      this._setCursorFromScalar(_clamp01(t) * nSeg * dur);
    }
    return this;
  }

  /**
   * Normalised playback position [0,1].
   * @returns {number}
   */
  time() {
    const nSeg = this.segments;
    if (nSeg === 0) return 0;
    const dur = Math.max(1, this.duration | 0);
    return _clamp01((this.seg * dur + this.f) / (nSeg * dur));
  }

  /**
   * Snapshot of transport state.
   * @returns {Object}
   */
  info() {
    return {
      keyframes: this.keyframes.length,
      segments:  this.segments,
      seg:       this.seg,
      f:         this.f,
      playing:   this.playing,
      loop:      this.loop,
      bounce:    this.bounce,
      rate:      this._rate,
      duration:  this.duration,
      time:      this.segments > 0 ? this.time() : 0
    };
  }

  /**
   * Advance cursor by rate frames.
   * Returns true while playing, false when stopping.
   * @returns {boolean}
   */
  tick() {
    if (!this.playing) return false;
    const nSeg = this.segments;
    if (nSeg === 0) {
      this.playing = false; this._onDeactivate?.(); return false;
    }
    if (this._rate === 0) return true;

    const dur   = Math.max(1, this.duration | 0);
    const total = nSeg * dur;
    const s     = _clampS(this.seg * dur + this.f, 0, total);
    const next  = s + this._rate * this._dir;

    // ── loop:true, bounce:true — bounce forever ───────────────────────────
    if (this.loop && this.bounce) {
      let pos = next, flips = 0;
      while (pos < 0 || pos > total) {
        if (pos < 0) { pos = -pos; flips++; }
        else         { pos = 2 * total - pos; flips++; }
      }
      if (flips & 1) this._dir = -this._dir;
      this._setCursorFromScalar(pos);
      return true;
    }

    // ── loop:false, bounce:true — bounce once, stop at origin ────────────
    if (!this.loop && this.bounce) {
      if (next >= total) {
        // far boundary: reflect and flip direction once
        this._setCursorFromScalar(Math.min(total, 2 * total - next));
        this._dir = -this._dir;
        this._bounced = true;
        return true;
      }
      if (next <= 0) {
        // origin: stop (whether we bounced or started backward)
        this._setCursorFromScalar(0);
        this.playing = false;
        this._dir = 1; this._bounced = false;
        if (typeof this.onEnd === 'function') { try { this.onEnd(this); } catch (_) {} }
        this._onEnd?.();
        this._onDeactivate?.();
        return false;
      }
      this._setCursorFromScalar(next);
      return true;
    }

    // ── loop:true, bounce:false — repeat forever ──────────────────────────
    if (this.loop) {
      this._setCursorFromScalar(((next % total) + total) % total);
      return true;
    }

    // ── loop:false, bounce:false — play once, stop at boundary ───────────
    if (next <= 0) {
      this._setCursorFromScalar(0);
      this.playing = false;
      if (typeof this.onEnd === 'function') { try { this.onEnd(this); } catch (_) {} }
      this._onEnd?.();
      this._onDeactivate?.();
      return false;
    }
    if (next >= total) {
      this._setCursorFromScalar(total);
      this.playing = false;
      if (typeof this.onEnd === 'function') { try { this.onEnd(this); } catch (_) {} }
      this._onEnd?.();
      this._onDeactivate?.();
      return false;
    }

    this._setCursorFromScalar(next);
    return true;
  }

  /** @private */
  _setCursorFromScalar(s) {
    const dur  = Math.max(1, this.duration | 0);
    const nSeg = this.segments;
    this.seg = Math.floor(s / dur);
    this.f   = s - this.seg * dur;
    if (this.seg >= nSeg) { this.seg = nSeg - 1; this.f = dur; }
    if (this.seg < 0)     { this.seg = 0;         this.f = 0;   }
  }
}

// =========================================================================
// S6  PoseTrack
// =========================================================================

/**
 * Renderer-agnostic TRS keyframe track.
 *
 * Keyframe shape: { pos:[x,y,z], rot:[x,y,z,w], scl:[x,y,z],
 *                   tanIn?:[x,y,z], tanOut?:[x,y,z] }
 *
 * tanIn  — incoming position tangent at this keyframe (Hermite mode).
 * tanOut — outgoing position tangent at this keyframe (Hermite mode).
 * When only one is supplied, the other mirrors it.
 * When neither is supplied, centripetal Catmull-Rom tangents are auto-computed.
 */
class PoseTrack extends Track {
  constructor() {
    super();
    /**
     * Position interpolation mode.
     * - 'hermite' — cubic Hermite; auto-computes centripetal Catmull-Rom tangents
     *               when none are stored (default)
     * - 'linear'  — lerp
     * - 'step'    — snap to k0; useful for discrete state changes
     * @type {'hermite'|'linear'|'step'}
     */
    this.posInterp = 'hermite';
    /**
     * Rotation interpolation mode.
     * - 'slerp'  — constant angular velocity (default)
     * - 'nlerp'  — normalised lerp; cheaper, slightly non-constant speed
     * - 'step'   — snap to k0 quaternion; useful for discrete state changes
     * @type {'slerp'|'nlerp'|'step'}
     */
    this.rotInterp = 'slerp';
    // Scratch for toMatrix() — avoids hot-path allocations
    this._pos = [0,0,0];
    this._rot = [0,0,0,1];
    this._scl = [1,1,1];
  }

  /**
   * Append one or more keyframes. Adjacent duplicates are skipped by default.
   * @param {Object|Object[]} spec
   * @param {{ deduplicate?: boolean }} [opts]
   */
  add(spec, opts) {
    if (Array.isArray(spec)) {
      for (const s of spec) this.add(s, opts);
      return;
    }
    const kf = _parseSpec(spec);
    if (!kf) return;
    const dedup = !opts || opts.deduplicate !== false;
    if (dedup && this.keyframes.length > 0) {
      if (_sameTransform(this.keyframes[this.keyframes.length - 1], kf)) return;
    }
    this.keyframes.push(kf);
  }

  /**
   * Replace (or append at end) the keyframe at index.
   * @param {number} index
   * @param {Object} spec
   * @returns {boolean}
   */
  set(index, spec) {
    if (!_isNum(index)) return false;
    const i = index | 0, kf = _parseSpec(spec);
    if (!kf || i < 0 || i > this.keyframes.length) return false;
    if (i === this.keyframes.length) this.keyframes.push(kf);
    else this.keyframes[i] = kf;
    return true;
  }

  /**
   * Evaluate interpolated TRS pose at current cursor.
   * @param {{ pos:number[], rot:number[], scl:number[] }} [out]
   * @returns {{ pos:number[], rot:number[], scl:number[] }} out
   */
  eval(out) {
    out = out || { pos:[0,0,0], rot:[0,0,0,1], scl:[1,1,1] };
    const n = this.keyframes.length;
    if (n === 0) return out;

    if (n === 1) {
      const k = this.keyframes[0];
      out.pos[0]=k.pos[0]; out.pos[1]=k.pos[1]; out.pos[2]=k.pos[2];
      out.rot[0]=k.rot[0]; out.rot[1]=k.rot[1]; out.rot[2]=k.rot[2]; out.rot[3]=k.rot[3];
      out.scl[0]=k.scl[0]; out.scl[1]=k.scl[1]; out.scl[2]=k.scl[2];
      return out;
    }

    const nSeg = n - 1;
    const dur  = Math.max(1, this.duration | 0);
    const seg  = _clampS(this.seg, 0, nSeg - 1);
    const t    = _clamp01(this.f / dur);
    const k0   = this.keyframes[seg];
    const k1   = this.keyframes[seg + 1];

    // pos — Hermite (auto-CR tangents when none stored), linear, or step
    if (this.posInterp === 'step') {
      out.pos[0]=k0.pos[0]; out.pos[1]=k0.pos[1]; out.pos[2]=k0.pos[2];
    } else if (this.posInterp === 'linear') {
      lerpVec3(out.pos, k0.pos, k1.pos, t);
    } else {
      const p0 = seg > 0      ? this.keyframes[seg - 1].pos : k0.pos;
      seg + 2 < n ? this.keyframes[seg + 2].pos  : k1.pos;
      const m0 = k0.tanOut != null ? k0.tanOut
               : k0.tanIn  != null ? k0.tanIn
               : _crTanOut(_m0, p0, k0.pos, k1.pos);
      const m1 = k1.tanIn  != null ? k1.tanIn
               : k1.tanOut != null ? k1.tanOut
               : _crTanIn(_m1, p0, k0.pos, k1.pos);
      hermiteVec3(out.pos, k0.pos, m0, k1.pos, m1, t);
    }

    // rot — step, slerp, or nlerp
    if (this.rotInterp === 'step') {
      out.rot[0]=k0.rot[0]; out.rot[1]=k0.rot[1]; out.rot[2]=k0.rot[2]; out.rot[3]=k0.rot[3];
    } else if (this.rotInterp === 'nlerp') {
      qNlerp(out.rot, k0.rot, k1.rot, t);
    } else {
      qSlerp(out.rot, k0.rot, k1.rot, t);
    }

    // scl — lerp
    lerpVec3(out.scl, k0.scl, k1.scl, t);

    return out;
  }

  /**
   * Evaluate into an existing column-major mat4.
   * @param {Float32Array|number[]} outMat4  16-element array.
   * @returns {Float32Array|number[]} outMat4
   */
  toMatrix(outMat4) {
    const xf = this.eval({ pos: this._pos, rot: this._rot, scl: this._scl });
    return transformToMat4(outMat4, xf);
  }
}

// =========================================================================
// S7  CameraTrack
// =========================================================================

/**
 * Lookat camera keyframe track.
 *
 * Keyframe shape: { eye:[x,y,z], center:[x,y,z], up:[x,y,z],
 *                   fov?:number, halfHeight?:number,
 *                   eyeTanIn?:[x,y,z], eyeTanOut?:[x,y,z],
 *                   centerTanIn?:[x,y,z], centerTanOut?:[x,y,z] }
 *
 * fov        — vertical fov (radians) for perspective cameras; null for ortho.
 * halfHeight — world-unit half-height of ortho frustum; null for perspective.
 * Both are optional and nullable. eval() lerps each only when both adjacent
 * keyframes carry a non-null value for that field.
 *
 * eyeTanIn/Out and centerTanIn/Out are optional vec3 tangents for Hermite
 * interpolation of the eye and center paths respectively.
 * When absent, centripetal Catmull-Rom tangents are auto-computed at eval time.
 *
 * Missing fields default to: center → [0,0,0], up → [0,1,0].
 *
 * add() accepts individual specs or a bulk array of specs:
 *
 *   { eye, center?, up?, fov?, halfHeight?,
 *     eyeTanIn?, eyeTanOut?, centerTanIn?, centerTanOut? }
 *
 * To capture a matrix-based pose, use PoseTrack.add({ mat4Model: mat4Eye })
 * for full-fidelity including roll, or cam.capturePose() for lookat-style.
 */
class CameraTrack extends Track {
  constructor() {
    super();
    /**
     * Eye-path interpolation mode.
     * @type {'hermite'|'linear'|'step'}
     */
    this.eyeInterp = 'hermite';
    /**
     * Center-path interpolation mode.
     * @type {'hermite'|'linear'|'step'}
     */
    this.centerInterp = 'linear';
  }

  /**
   * Append one or more camera keyframes. Adjacent duplicates are skipped by default.
   * @param {Object|Object[]} spec
   * @param {{ deduplicate?: boolean }} [opts]
   */
  add(spec, opts) {
    if (Array.isArray(spec)) {
      for (const s of spec) this.add(s, opts);
      return;
    }
    const kf = _parseCameraSpec(spec);
    if (!kf) return;
    const dedup = !opts || opts.deduplicate !== false;
    if (dedup && this.keyframes.length > 0) {
      if (_sameCameraKeyframe(this.keyframes[this.keyframes.length - 1], kf)) return;
    }
    this.keyframes.push(kf);
  }

  /**
   * Replace (or append at end) the camera keyframe at index.
   * @param {number} index
   * @param {Object} spec
   * @returns {boolean}
   */
  set(index, spec) {
    if (!_isNum(index)) return false;
    const i = index | 0, kf = _parseCameraSpec(spec);
    if (!kf || i < 0 || i > this.keyframes.length) return false;
    if (i === this.keyframes.length) this.keyframes.push(kf);
    else this.keyframes[i] = kf;
    return true;
  }

  /**
   * Evaluate interpolated camera pose at current cursor.
   *
   * @param {{ eye:number[], center:number[], up:number[] }} [out]
   * @returns {{ eye:number[], center:number[], up:number[] }} out
   */
  eval(out) {
    out = out || { eye:[0,0,0], center:[0,0,0], up:[0,1,0], fov:null, halfHeight:null };
    const n = this.keyframes.length;
    if (n === 0) return out;

    if (n === 1) {
      const k = this.keyframes[0];
      out.eye[0]=k.eye[0];       out.eye[1]=k.eye[1];       out.eye[2]=k.eye[2];
      out.center[0]=k.center[0]; out.center[1]=k.center[1]; out.center[2]=k.center[2];
      out.up[0]=k.up[0];         out.up[1]=k.up[1];         out.up[2]=k.up[2];
      out.fov        = k.fov;
      out.halfHeight = k.halfHeight;
      return out;
    }

    const nSeg = n - 1;
    const dur  = Math.max(1, this.duration | 0);
    const seg  = _clampS(this.seg, 0, nSeg - 1);
    const t    = _clamp01(this.f / dur);
    const k0   = this.keyframes[seg];
    const k1   = this.keyframes[seg + 1];

    // eye — Hermite (auto-CR tangents when none stored), linear, or step
    if (this.eyeInterp === 'step') {
      out.eye[0]=k0.eye[0]; out.eye[1]=k0.eye[1]; out.eye[2]=k0.eye[2];
    } else if (this.eyeInterp === 'linear') {
      lerpVec3(out.eye, k0.eye, k1.eye, t);
    } else {
      const p0 = seg > 0      ? this.keyframes[seg - 1].eye : k0.eye;
      seg + 2 < n ? this.keyframes[seg + 2].eye  : k1.eye;
      const m0 = k0.eyeTanOut != null ? k0.eyeTanOut
               : k0.eyeTanIn  != null ? k0.eyeTanIn
               : _crTanOut(_m0, p0, k0.eye, k1.eye);
      const m1 = k1.eyeTanIn  != null ? k1.eyeTanIn
               : k1.eyeTanOut != null ? k1.eyeTanOut
               : _crTanIn(_m1, p0, k0.eye, k1.eye);
      hermiteVec3(out.eye, k0.eye, m0, k1.eye, m1, t);
    }

    // center — Hermite, linear, or step (independent lookat target)
    if (this.centerInterp === 'step') {
      out.center[0]=k0.center[0]; out.center[1]=k0.center[1]; out.center[2]=k0.center[2];
    } else if (this.centerInterp === 'hermite') {
      const c0 = seg > 0      ? this.keyframes[seg - 1].center : k0.center;
      seg + 2 < n ? this.keyframes[seg + 2].center  : k1.center;
      const m0 = k0.centerTanOut != null ? k0.centerTanOut
               : k0.centerTanIn  != null ? k0.centerTanIn
               : _crTanOut(_m0, c0, k0.center, k1.center);
      const m1 = k1.centerTanIn  != null ? k1.centerTanIn
               : k1.centerTanOut != null ? k1.centerTanOut
               : _crTanIn(_m1, c0, k0.center, k1.center);
      hermiteVec3(out.center, k0.center, m0, k1.center, m1, t);
    } else {
      lerpVec3(out.center, k0.center, k1.center, t);
    }

    // up — nlerp on unit sphere
    lerpVec3(out.up, k0.up, k1.up, t);
    const ul=Math.sqrt(out.up[0]*out.up[0]+out.up[1]*out.up[1]+out.up[2]*out.up[2])||1;
    out.up[0]/=ul; out.up[1]/=ul; out.up[2]/=ul;

    // fov / halfHeight — lerp when both keyframes carry non-null values
    out.fov = (k0.fov != null && k1.fov != null)
      ? k0.fov + t * (k1.fov - k0.fov) : (k0.fov ?? k1.fov ?? null);
    out.halfHeight = (k0.halfHeight != null && k1.halfHeight != null)
      ? k0.halfHeight + t * (k1.halfHeight - k0.halfHeight)
      : (k0.halfHeight ?? k1.halfHeight ?? null);

    return out;
  }
}

/**
 * @file Frustum planes and visibility tests — zero allocations.
 * @module tree/visibility
 * @license AGPL-3.0-only
 *
 * Planes are a flat Float64Array(24): 6 planes × 4 floats [a, b, c, d].
 * All inputs are scalars. All outputs are INVISIBLE | VISIBLE | SEMIVISIBLE.
 */


// Plane indices
const PLANE_LEFT = 0, PLANE_RIGHT = 1, PLANE_NEAR = 2,
             PLANE_FAR = 3, PLANE_TOP = 4, PLANE_BOTTOM = 5;

/**
 * Compute 6 frustum planes from camera basis (world space) + projection params.
 * All inputs are scalars — the addon extracts them from the inverse view matrix
 * and projection queries before calling.
 *
 * @param {Float64Array} out  24-float output.
 * @param {number} posX,posY,posZ  Camera world position.
 * @param {number} vdX,vdY,vdZ    View direction (−Z in eye space, world).
 * @param {number} upX,upY,upZ    Camera up.
 * @param {number} rtX,rtY,rtZ    Camera right.
 * @param {boolean} ortho         true if orthographic.
 * @param {number} near,far,left,right,top,bottom
 *   Projection extents in camera space.
 *   Sign contract: top > 0, bottom < 0, right > 0, left < 0 for standard y-up camera.
 */
function frustumPlanes(
  out,
  posX, posY, posZ,
  vdX, vdY, vdZ,
  upX, upY, upZ,
  rtX, rtY, rtZ,
  ortho,
  near, far, left, right, top, bottom
) {
  const posViewDir = posX*vdX + posY*vdY + posZ*vdZ;
  const posRight   = posX*rtX + posY*rtY + posZ*rtZ;
  const posUp      = posX*upX + posY*upY + posZ*upZ;

  if (ortho) {
    // Left: normal = −right
    out[0] = -rtX; out[1] = -rtY; out[2] = -rtZ;
    out[3] = -(posRight - left) * -1; // dot(pos - right*left, -right) ... simplified:
    // Actually: d = dot(pointOnPlane, normal)
    // pointOnPlane = pos + right*left (left is negative for standard ortho)
    // normal = -right
    // d = dot(pos + right*left, -right) = -posRight - left
    out[3] = -posRight - left;

    // Right: normal = right
    out[4] = rtX; out[5] = rtY; out[6] = rtZ;
    out[7] = posRight + right;

    // Top: normal = up
    out[16] = upX; out[17] = upY; out[18] = upZ;
    out[19] = posUp + top;

    // Bottom: normal = -up
    out[20] = -upX; out[21] = -upY; out[22] = -upZ;
    out[23] = -posUp - bottom;
  } else {
    // Left
    const hfovl = Math.atan2(left, near);
    const shfovl = Math.sin(hfovl), chfovl = Math.cos(hfovl);
    out[0] = vdX*shfovl - rtX*chfovl;
    out[1] = vdY*shfovl - rtY*chfovl;
    out[2] = vdZ*shfovl - rtZ*chfovl;
    out[3] = shfovl*posViewDir - chfovl*posRight;

    // Right
    const hfovr = Math.atan2(right, near);
    const shfovr = Math.sin(hfovr), chfovr = Math.cos(hfovr);
    out[4] = -vdX*shfovr + rtX*chfovr;
    out[5] = -vdY*shfovr + rtY*chfovr;
    out[6] = -vdZ*shfovr + rtZ*chfovr;
    out[7] = -shfovr*posViewDir + chfovr*posRight;

    // Top
    const fovt = Math.atan2(top, near);
    const sfovt = Math.sin(fovt), cfovt = Math.cos(fovt);
    out[16] = -vdX*sfovt + upX*cfovt;
    out[17] = -vdY*sfovt + upY*cfovt;
    out[18] = -vdZ*sfovt + upZ*cfovt;
    out[19] = -sfovt*posViewDir + cfovt*posUp;

    // Bottom
    const fovb = Math.atan2(bottom, near);
    const sfovb = Math.sin(fovb), cfovb = Math.cos(fovb);
    out[20] = vdX*sfovb - upX*cfovb;
    out[21] = vdY*sfovb - upY*cfovb;
    out[22] = vdZ*sfovb - upZ*cfovb;
    out[23] = sfovb*posViewDir - cfovb*posUp;
  }

  // Near plane: normal = −viewDir
  out[8]  = -vdX; out[9]  = -vdY; out[10] = -vdZ;
  out[11] = -posViewDir - near;

  // Far plane: normal = viewDir
  out[12] = vdX; out[13] = vdY; out[14] = vdZ;
  out[15] = posViewDir + far;

  return out;
}

/**
 * Signed distance from point to one frustum plane.
 * @param {Float64Array} planes  24-float planes buffer.
 * @param {number} planeIdx  0–5 (LEFT, RIGHT, NEAR, FAR, TOP, BOTTOM).
 * @param {number} px,py,pz  Point coordinates.
 * @returns {number}
 */
function distanceToPlane(planes, planeIdx, px, py, pz) {
  const b = planeIdx * 4;
  return planes[b]*px + planes[b+1]*py + planes[b+2]*pz - planes[b+3];
}

/** @returns {number} INVISIBLE | VISIBLE | SEMIVISIBLE */
function pointVisibility(planes, px, py, pz) {
  for (let i = 0; i < 6; i++) {
    if (distanceToPlane(planes, i, px, py, pz) > 0) return INVISIBLE;
  }
  return VISIBLE;
}

/** @returns {number} INVISIBLE | VISIBLE | SEMIVISIBLE */
function sphereVisibility(planes, cx, cy, cz, radius) {
  let allIn = true;
  for (let i = 0; i < 6; i++) {
    const d = distanceToPlane(planes, i, cx, cy, cz);
    if (d > radius) return INVISIBLE;
    if (d > 0 || -d < radius) allIn = false;
  }
  return allIn ? VISIBLE : SEMIVISIBLE;
}

/** @returns {number} INVISIBLE | VISIBLE | SEMIVISIBLE */
function boxVisibility(planes, x0, y0, z0, x1, y1, z1) {
  let allIn = true;
  for (let i = 0; i < 6; i++) {
    const b = i * 4;
    const a = planes[b], bv = planes[b+1], c = planes[b+2], d = planes[b+3];
    let allOut = true;
    for (let corner = 0; corner < 8; corner++) {
      const cx = (corner & 4) ? x0 : x1;
      const cy = (corner & 2) ? y0 : y1;
      const cz = (corner & 1) ? z0 : z1;
      const dist = a*cx + bv*cy + c*cz - d;
      if (dist > 0) { allIn = false; }
      else { allOut = false; }
    }
    if (allOut) return INVISIBLE;
  }
  return allIn ? VISIBLE : SEMIVISIBLE;
}

export { CameraTrack, EYE, INVISIBLE, MATRIX, MODEL, NDC, ORIGIN, PLANE_BOTTOM, PLANE_FAR, PLANE_LEFT, PLANE_NEAR, PLANE_RIGHT, PLANE_TOP, PoseTrack, SCREEN, SEMIVISIBLE, VISIBLE, WEBGL, WEBGPU, WORLD, _i, _j, _k, boxVisibility, distanceToPlane, frustumPlanes, hermiteVec3, i, j, k, lerpVec3, mapDirection, mapLocation, mat3Direction, mat3NormalFromMat4, mat4Bias, mat4Eye, mat4FromBasis, mat4FromScale, mat4FromTRS, mat4FromTranslation, mat4Invert, mat4Location, mat4MV, mat4Mul, mat4MulDir, mat4MulPoint, mat4Ortho, mat4PV, mat4Persp, mat4Pick, mat4Reflect, mat4ToRotation, mat4ToScale, mat4ToTransform, mat4ToTranslation, mat4View, pixelRatio, pointVisibility, projBottom, projFar, projFov, projHfov, projIsOrtho, projLeft, projNear, projRight, projTop, qCopy, qDot, qFromAxisAngle, qFromLookDir, qFromMat4, qFromRotMat3x3, qMul, qNegate, qNlerp, qNormalize, qSet, qSlerp, qToMat4, quatToAxisAngle, sphereVisibility, transformToMat4 };
//# sourceMappingURL=index.js.map
